import {
  default as superAgentRequest,
  ProgressEvent,
  SuperAgentStatic,
  SuperAgentRequest,
  Response,
} from 'superagent';

import {getGlobalRequestHandlers} from './utilities/globalRequestHandlers';
import {blankToNull} from './notBlank';

export type RequestHeaders = {
  [header: string]: string;
};
export type RequestHeadersHandler = (headers: RequestHeaders) => RequestHeaders;

export type ConfigureAgentHandler = (
  agent: SuperAgentStatic
) => SuperAgentStatic;

export type ConfigureRequestHandler = (
  agent: SuperAgentRequest
) => SuperAgentRequest;

export type CallbackHandler = (err: any, res?: Response) => void;

export type AddressInResponse = {
  addressClassificationId: string;
  formattedAddressLines: string;
  id: string;
};

export type Billability = 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';

export type BookingType =
  | 'SETTLEMENT'
  | 'PREPAYMENT_SETTLEMENT'
  | 'WRITE_OFF'
  | 'PAYMENT_REVOCATION'
  | 'UNDERPAYMENT_ROUNDING_DIFFERENCE'
  | 'OVERPAYMENT_ROUNDING_DIFFERENCE'
  | 'REPAYMENT'
  | 'SETTLEMENT_CANCELLATION'
  | 'OVERPAYMENT_ROUNDING_DIFFERENCE_CANCELLATION'
  | 'TRANSFER';

export type CalendarWeekWorktimeInResponse = {
  calendarWeek: number;
  worktime: WorktimeInResponse;
};

export type CasusArchiveDestroyParams = {
  date: string;
  summary?: string;
};

export type CasusArchiveDetail = {
  archiveLocation?: string;
  date: string;
  description?: string;
  state: CasusArchiveState;
};

export type CasusArchiveParams = {
  archiveReference?: string;
  archivingLocationId: string;
  archivingNumber?: number;
  date: string;
  destroyableFrom?: string;
  doNotDestroy: boolean;
  summary?: string;
};

export type CasusArchiveState =
  | 'ORDERED'
  | 'IN_PROGRESS'
  | 'CREATED'
  | 'RETRIEVED'
  | 'ABANDONED';

export type CasusArchiveUpdateParams = {
  archiveReference?: string;
  archivingLocationId: string;
  date: string;
  destroyableFrom?: string;
  doNotDestroy: boolean;
  summary?: string;
};

export type CasusArchivesResponse = {
  archives: Array<CasusArchiveDetail>;
};

export type CasusArchivesSortField =
  | 'DATE'
  | 'STATUS'
  | 'DESCRIPTION'
  | 'ARCHIVE_LOCATION';

export type CasusArchivingLocationResponse = {
  active: boolean;
  activeFrom?: string;
  activeTo?: string;
  canDelete: boolean;
  id: string;
  name: string;
};

export type CasusArchivingLog = {
  archiveReference?: string;
  archivingLocationId?: string;
  archivingNumber?: number;
  casusId: string;
  casusName?: string;
  clientName?: string;
  createdAt: string;
  createdByUserId: string;
  date: string;
  destroyableFrom?: string;
  doNotDestroy: boolean;
  groupName?: string;
  id: string;
  state: CasusArchivingState;
  summary?: string;
  version: number;
};

export type CasusArchivingNotPossibleCondition = {
  conditionType: string;
  reason: CasusArchivingNotPossibleReason;
};

export type CasusArchivingNotPossibleReason =
  | 'OPEN_INVOICE_CONTAINER'
  | 'OPEN_INVOICE'
  | 'OPEN_PAYMENT'
  | 'OPEN_FEE'
  | 'OPEN_CLIENT_MONEY_DEBTOR';

export type CasusArchivingState = 'UNARCHIVED' | 'ARCHIVED' | 'DESTROYED';

export type CasusArchivingWarnings = {
  casusArchivingNotPossibleReasons: Array<CasusArchivingNotPossibleReason>;
  casusArchivingWarningReasons: Array<CasusArchivingNotPossibleReason>;
};

export type CasusAssortmentResponse = {
  favorites: Array<MinimalCasusInResponse>;
  recents: Array<MinimalCasusInResponse>;
};

export type CasusCharacteristicsInParams = {
  casusName: string;
  clientReference: ClientReference;
  description?: string;
  disclosureLevel: CasusDisclosureLevel;
  fileNumber?: string;
  procedureNumber?: string;
};

export type CasusCharacteristicsInResponse = {
  casusDisplayNameWithClient: string;
  casusName: string;
  clientDisplayName: string;
  clientReference: ClientReference;
  description?: string;
  disclosureLevel: CasusDisclosureLevel;
  fileNumber?: string;
  procedureNumber?: string;
};

export type CasusCreateCharacteristicsParams = {
  casusGroupId?: string;
  casusName: string;
  client: CasusCreateClientParams;
  description?: string;
  disclosureLevel: CasusDisclosureLevel;
  fileNumber?: string;
  procedureNumber?: string;
};

export type CasusCreateClientParams = {
  adHocClientName?: string;
  clientId?: string;
  contactId?: string;
  newContact?: ContactStoringParams;
};

export type CasusCreationCondition = {
  conditionType: 'NonUniqueCasusNameCondition' | 'DuplicateContactCondition';
};

export type CasusDebtorBooking = {
  amount: Money;
  bookingGroupId?: string;
  canBeRevokedFromInvoice: boolean;
  createdAt: string;
  createdBy: UserInResponse;
  date: string;
  id?: string;
  invoiceId?: string;
  paymentId?: string;
  summary: string;
  type: BookingType;
  uniqueNumber: string;
};

export type CasusDebtorContactIds = {
  addressContactId: string;
  addressPostalAddressId: string;
  addresseeContactId: string;
};

export type CasusDebtorEmailAddress = {
  emailAddressId: string;
  emailContactId: string;
};

export type CasusDebtorFinancialOverviewInResponse = {
  balance: Money;
  sumInvoicesRemainingAmount: Money;
  sumPaymentsRemainingAmount: Money;
};

export type CasusDebtorInResponse = {
  bankAccountId: string;
  contactIds: CasusDebtorContactIds;
  defaultInvoiceRecipient?: DefaultInvoiceRecipientInResponse;
  deliveryContactIds?: CasusDebtorContactIds;
  displayAddressLines: string;
  displayDeliveryAddressLines?: string;
  displayEmailAddress?: string;
  displayName: string;
  domesticallyBased: boolean;
  emailAddress?: CasusDebtorEmailAddress;
  financialOverview: CasusDebtorFinancialOverviewInResponse;
  id: string;
  taxRate?: TaxRate;
};

export type CasusDebtorInUseCondition = {
  casusDebtorId: string;
  conditionType: 'CasusDebtorInUseCondition';
};

export type CasusDebtorInvoice = {
  bookings: Array<CasusDebtorBooking>;
  createdAt: string;
  createdBy: UserInResponse;
  date: string;
  id: string;
  remainingAmount: Money;
  state: InvoiceState;
  summary: string;
  totalAfterTax: Money;
  uniqueNumber: string;
};

export type CasusDebtorInvoiceSortField =
  | 'VALUE_DATE'
  | 'CREATED_AT'
  | 'TOTAL_AFTER_TAX'
  | 'REMAINING_AMOUNT';

export type CasusDebtorPayment = {
  createdAt: string;
  createdBy: UserInResponse;
  date: string;
  id: string;
  paymentBookings: Array<CasusDebtorBooking>;
  prepaymentType?: PrepaymentType;
  referenceNumber?: string;
  remainingAmount: Money;
  state: PaymentConsumedState;
  summary: string;
  totalAmount: Money;
  uniqueNumber: string;
};

export type CasusDebtorPaymentSortField =
  | 'VALUE_DATE'
  | 'CREATED_AT'
  | 'TOTAL_AMOUNT'
  | 'REMAINING_AMOUNT';

export type CasusDebtorStoringParams = {
  bankAccountId: string;
  contactIds: CasusDebtorContactIds;
  defaultInvoiceRecipient: boolean;
  deliveryContactIds?: CasusDebtorContactIds;
  domesticallyBased: boolean;
  emailAddress?: CasusDebtorEmailAddress;
  taxRate?: TaxRate;
};

export type CasusDebtorUpdateParams = {
  bankAccountId: string;
  defaultInvoiceRecipient: boolean;
  deliveryContactIds?: CasusDebtorContactIds;
  domesticallyBased: boolean;
  emailAddress?: CasusDebtorEmailAddress;
  taxRate?: TaxRate;
};

export type CasusDeletionNotPossibleReason =
  | 'ARCHIVE_EXISTS'
  | 'DOCUMENTS_EXIST'
  | 'INVOICES_EXIST'
  | 'PAYMENTS_EXIST'
  | 'CLIENT_MONEY_EXISTS'
  | 'ACCOUNTING_RECEIPTS_EXIST'
  | 'FEES_EXIST'
  | 'EMAILS_EXIST'
  | 'ACTIVITIES_EXIST'
  | 'JUSTITIA_SUBMISSIONS_EXIST'
  | 'OBLIGATIONS_EXIST'
  | 'CASUS_LOCKED';

export type CasusDisclosureLevel = 'PUBLIC' | 'PROTECTED';

export type CasusGroup = {
  clientId: string;
  id: string;
  name: string;
};

export type CasusGroupCreationParams = {
  casusIds: Array<string>;
  name: string;
};

export type CasusGroupNameValidationResponse = {
  empty: boolean;
  maxLengthViolation?: number;
  nameExists: boolean;
};

export type CasusGroupUpdateParams = {
  name: string;
};

export type CasusInResponse = {
  active: boolean;
  alertMessage?: string;
  authorizedGroups: Array<GroupInResponse>;
  authorizedUsers: Array<UserInResponse>;
  casusNumber: string;
  clientDisplayName: string;
  collaborators: Array<CollaboratorInResponse>;
  currency: string;
  description?: string;
  disclosureLevel: CasusDisclosureLevel;
  displayNameWithClient: string;
  displayNameWithClientAndNumber: string;
  favorite: boolean;
  id: string;
  internal: boolean;
  invoiceable: boolean;
  locale?: string;
  locked: boolean;
  name: string;
  readonly: boolean;
  responsiblePerson: UserInResponse;
  state: CasusState;
};

export type CasusInvoiceableFilter = 'ALL' | 'INVOICEABLE' | 'NOT_INVOICEABLE';

export type CasusLockedFilter = 'ALL' | 'LOCKED' | 'UNLOCKED';

export type CasusMasterData = {
  casusArchivingLocations: Array<TranslatedCasusArchivingLocation>;
  collaboratorRoles: Array<TranslatedCollaboratorRole>;
  involvedPartyRoles: Array<TranslatedInvolvedPartyRole>;
  officeLocations: Array<TranslatedOfficeLocation>;
  specialityFields: Array<TranslatedSpecialityField>;
};

export type CasusNotFoundCondition = {
  conditionType: string;
};

export type CasusProtectedFilter = 'ALL' | 'PROTECTED' | 'PUBLIC';

export type CasusSearchItem = {
  active: boolean;
  casusNumber: string;
  correspondenceLocale: string;
  displayNameWithClient: string;
  displayNameWithClientAndNumber: string;
  id: string;
  internal: boolean;
  invoiceable: boolean;
  isFavorite?: boolean;
  locked: boolean;
  protected: boolean;
  readonly: boolean;
  rootFolderId: string;
  state: CasusState;
};

export type CasusSettingsPermissions = {
  canManageCasusProtection: boolean;
  canModifyExportable: boolean;
  canModifyInvoiceable: boolean;
};

export type CasusState = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';

export type CasusStateFilter = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';

export type CasusTemplateAuthorizedGroups = {
  groups: Array<string>;
};

export type CasusTemplateCreatedResponse = {
  id: string;
};

export type CasusTemplateFolder = {
  name: string;
  subFolders: Array<CasusTemplateFolder>;
};

export type CasusTemplateGroupHourlyRate = {
  billability: Billability;
  hourlyRate: Money;
  id: string;
};

export type CasusTemplateHourlyRate = {
  billability: Billability;
  hourlyRate: Money;
};

export type CasusTemplateHourlyRateParams = {
  groupRates: Array<CasusTemplateGroupHourlyRate>;
  rate?: CasusTemplateHourlyRate;
};

export type CasusTemplateHourlyRatesResponse = {
  groupRates: Array<UserOrGroupHourlyRateSettings>;
  rate?: HourlyRateSettings;
};

export type CasusTemplateInvoiceParams = {
  exportable?: boolean;
  invoiceFeePercentage?: string;
  invoiceSignatoryUsers: Array<string>;
  invoiceTemplateFileId?: string;
  invoiceable?: boolean;
  vatNumberId?: string;
};

export type CasusTemplateOverviewResponse = {
  createdAt: string;
  createdBy: UserInResponse;
  id: string;
  name: string;
  published: boolean;
  uniqueIdentifier: number;
};

export type CasusTemplateParams = {
  correspondenceLocale?: string;
  internal?: boolean;
  name: string;
  officeLocationId?: string;
  published: boolean;
  specialityFieldId?: string;
};

export type CasusTemplateResponse = {
  authorizedGroups: Array<GroupInResponse>;
  correspondenceLocale?: string;
  createdAt: string;
  createdBy: UserInResponse;
  folderStructure: CasusTemplateFolder;
  hourlyRates: CasusTemplateHourlyRatesResponse;
  id: string;
  internal?: boolean;
  invoiceSettings: InvoiceSettings;
  name: string;
  officeLocationId?: string;
  published: boolean;
  specialityFieldId?: string;
  uniqueIdentifier: number;
};

export type CasusTemplateSortField =
  | 'NAME'
  | 'UNIQUE_IDENTIFIER'
  | 'PUBLISHED'
  | 'CREATED_AT';

export type CasusType = 'ALL' | 'INTERNAL' | 'NOT_INTERNAL';

export type CasusUnarchiveParams = {
  date: string;
  summary?: string;
};

export type CheckNameAvailabilityResponse = {
  available: boolean;
};

export type ClientReference = {
  adHocClientName?: string;
  contactId?: string;
};

export type ClientType = 'CONTACT' | 'AD_HOC';

export type ClientWithGroups = {
  clientId: string;
  sortedGroups: Array<CasusGroup>;
};

export type CockpitFinanceOverviewResponse = {
  clientMoneyRemainingAmount: Money;
  openFeesTotalDuration: number;
  openInvoices: FinanceOverviewAmounts;
  overdueInvoices: FinanceOverviewAmounts;
  paidInvoicesAmount: Money;
  paymentsRemainingAmount: Money;
  totalInvoicedAmountAfterTax: Money;
};

export type CockpitInvolvedPeopleResponse = {
  involvedPeople: Array<InvolvedPersonInResponse>;
};

export type CockpitRecentDocumentsResponse = {
  recentDocuments: Array<DocumentInResponse>;
};

export type CockpitTimelineEventsResponse = {
  events: PageEventInResponse;
};

export type CockpitTimetrackingMonthResponse = {
  weeks: Array<CalendarWeekWorktimeInResponse>;
};

export type CockpitTimetrackingWeekResponse = {
  daysStartingWithMonday: Array<WorktimeInResponse>;
  nominalDailyWorktime: number;
};

export type CockpitTimetrackingYearResponse = {
  monthsStartingWithJanuary: Array<WorktimeInResponse>;
};

export type CollaboratorDetailInResponse = {
  collaboratorRoleId: string;
  id: string;
};

export type CollaboratorInResponse = {
  collaboratorDetails: Array<CollaboratorDetailInResponse>;
  id: string;
  user: UserInResponse;
};

export type CollaboratorParams = {
  collaboratorRoleIds: Array<string>;
  userId: string;
};

export type ContactInResponse = {
  addresses: Array<AddressInResponse>;
  displayName: string;
  emailAddresses: Array<EmailAddressInResponse>;
  id: string;
  links: Array<LinkInResponse>;
  phoneNumbers: Array<PhoneNumberInResponse>;
  type: ContactType;
};

export type ContactStoringParams = {
  legalEntityAttributes?: LegalEntityAttributes;
  naturalPersonAttributes?: NaturalPersonAttributes;
};

export type ContactType = 'NATURAL_PERSON' | 'LEGAL_ENTITY';

export type ContactsResponse = {
  adHocClientName?: string;
  casusDebtors: Array<CasusDebtorInResponse>;
  clientContact?: ContactInResponse;
  involvedParties: Array<InvolvedPartyInResponse>;
};

export type CreateCasusArchivingLocationParams = {
  activeFrom?: string;
  activeTo?: string;
  name: string;
};

export type CreateCasusParams = {
  casusTemplateId?: string;
  characteristics: CasusCreateCharacteristicsParams;
  startDate: string;
};

export type CreateCasusResponse = {
  createdCasusId: string;
};

export type CreateOfficeLocationParams = {
  activeFrom?: string;
  activeTo?: string;
  legacyReference?: number;
  name: string;
};

export type CreateSpecialityFieldParams = {
  activeFrom?: string;
  activeTo?: string;
  translations: Array<SpecialityFieldTranslationParams>;
};

export type CreatedCasusArchivingLocationResponse = {
  id: string;
};

export type CreatedOfficeLocationResponse = {
  id: string;
};

export type DefaultInvoiceRecipientFractionInParams = {
  defaultInvoiceRecipientId: string;
  fractionAsDecimal: string;
};

export type DefaultInvoiceRecipientFractionsUpdateParams = {
  recipientFractions: Array<DefaultInvoiceRecipientFractionInParams>;
};

export type DefaultInvoiceRecipientInResponse = {
  fractionAsDecimal: string;
  id: string;
};

export type DefaultInvoiceRecipientsResponse = {
  casusDebtors: Array<MinimalCasusDebtorInResponse>;
};

export type DeleteCasusCheckResult = {
  possible: boolean;
  reasons: Array<CasusDeletionNotPossibleReason>;
};

export type DocumentInResponse = {
  casusDisplayNameWithClient: string;
  casusId: string;
  displayName: string;
  documentType: DocumentType;
  folderId: string;
  id: string;
  lastModifiedAt: string;
  lastModifiedBy: UserInResponse;
};

export type DocumentSourceObject = {
  deleted: boolean;
  documentId?: string;
  documentVersionId: string;
  folderId?: string;
};

export type DocumentType =
  | 'EMAIL_DOCUMENT'
  | 'EXCEL_DOCUMENT'
  | 'POWERPOINT_DOCUMENT'
  | 'PDF_DOCUMENT'
  | 'WORD_DOCUMENT'
  | 'IMAGE_DOCUMENT'
  | 'TEXT_DOCUMENT'
  | 'RTF_DOCUMENT'
  | 'ZIP_DOCUMENT'
  | 'UNKNOWN_DOCUMENT';

export type DuplicateContact = {
  addressOnASingleLine: string;
  displayAddressee: string;
  id: string;
};

export type DuplicateContactCondition = {
  conditionType: 'DuplicateContactCondition';
  existingContacts: Array<DuplicateContact>;
};

export type EmailAddressInResponse = {
  address: string;
  emailClassificationId: string;
  id: string;
};

export type EventContext = 'DOCUMENT' | 'OBLIGATION' | 'TASK' | 'NOTE';

export type EventInResponse = {
  casusDisplayNameWithClient: string;
  casusId: string;
  context: EventContext;
  date: string;
  eventType: EventType;
  id: string;
  sourceObject: SourceObject;
  text: string;
};

export type EventType =
  | 'OBLIGATION_DEADLINE'
  | 'OBLIGATION_VERSION_APPROVED'
  | 'OBLIGATION_VERSION_REJECTED'
  | 'TASK_DUE'
  | 'DOCUMENT_CREATED'
  | 'DOCUMENT_SHARED'
  | 'DOCUMENT_EDITED'
  | 'NOTE_CREATED';

export type FavoriteParams = {
  favorite: boolean;
};

export type FinanceOverviewAmounts = {
  openAmount: Money;
  paidAmount: Money;
  totalAmount: Money;
};

export type GetCasusResponse = {
  casus: CasusInResponse;
};

export type GetRelatedAddressesResponse = {
  addressesOfReferenceContact: Array<AddressInResponse>;
  relatedContactAddresses: Array<RelatedContactAddressInResponse>;
};

export type GetRelatedEmailAddressesResponse = {
  emailAddressesOfReferenceContact: Array<EmailAddressInResponse>;
  relatedContactEmailAddresses: Array<RelatedContactEmailAddressInResponse>;
};

export type GroupInResponse = {
  id: string;
  name: string;
};

export type HourlyRateSettings = {
  billability: Billability;
  hourlyRate: Money;
};

export type ImportCasusParams = {
  active: boolean;
  casusName: string;
  clientName: string;
  responsibleUserName: string;
  startDate: string;
};

export type ImportCasusResponse = {
  createdCasusId: string;
  createdRootFolderId: string;
};

export type InvoiceSettings = {
  defaultPercentageFeeAsDecimal?: string;
  exportable: boolean;
  invoiceable: boolean;
  signatoryUsers: Array<UserInResponse>;
  templateFile?: TemplateFile;
  vatNumberId?: string;
};

export type InvoiceState =
  | 'REVIEW'
  | 'SENT'
  | 'OVERDUE'
  | 'DONE'
  | 'CANCELLED'
  | 'CANCELLATION';

export type InvolvedPartyInResponse = {
  addressContact?: ContactInResponse;
  contact: ContactInResponse;
  id: string;
  involvedPartyAddress?: AddressInResponse;
  involvedPartyRoleId: string;
  referenceNumber?: string;
};

export type InvolvedPartyResponse = {
  involvedParty: InvolvedPartyInResponse;
};

export type InvolvedPartyStoringParams = {
  contactId: string;
  involvedPartyAddressId?: string;
  involvedPartyRoleId: string;
  referenceNumber?: string;
};

export type InvolvedPersonInResponse = {
  addressOnASingleLine?: string;
  collaboratorRoleId?: string;
  involvedPartyId?: string;
  involvedPartyRoleId?: string;
  involvedPersonType: InvolvedPersonType;
  name: string;
};

export type InvolvedPersonType =
  | 'CLIENT'
  | 'RESPONSIBLE_USER'
  | 'INVOLVED_PARTY'
  | 'CASUS_DEBTOR'
  | 'COLLABORATOR';

export type LatestCasusArchivingLogResponse = {
  casusArchivingLog?: CasusArchivingLog;
  casusArchivingWarnings: CasusArchivingWarnings;
};

export type LegalEntityAttributes = {
  name: string;
};

export type LinkInResponse = {
  id: string;
  linkClassificationId: string;
  uri: string;
};

export type MinimalCasusDebtorInResponse = {
  casusDisplayNameWithClient: string;
  displayName: string;
  id: string;
};

export type MinimalCasusInResponse = {
  active: boolean;
  casusNumber: string;
  clientContactType?: ContactType;
  clientDisplayName: string;
  clientId: string;
  disclosureLevel: CasusDisclosureLevel;
  displayNameWithClient: string;
  displayNameWithClientAndNumber: string;
  id: string;
  internal: boolean;
  name: string;
};

export type MinimalCasusWithGroupInResponse = {
  active: boolean;
  casusGroupId?: string;
  casusGroupName?: string;
  casusNumber: string;
  clientContactType?: ContactType;
  clientDisplayName: string;
  clientId: string;
  disclosureLevel: CasusDisclosureLevel;
  displayNameWithClient: string;
  displayNameWithClientAndNumber: string;
  id: string;
  internal: boolean;
  locked: boolean;
  name: string;
  readonly: boolean;
  state: CasusState;
};

export type MinimalClient = {
  contactType?: ContactType;
  displayName: string;
  id: string;
};

export type MinimalContactInResponse = {
  displayName: string;
  id: string;
  type: ContactType;
};

export type Money = {
  amount: string;
};

export type MostRecentlyUsedParams = {
  casusId: string;
};

export type NaturalPersonAttributes = {
  givenNames: string;
  lastNames: string;
};

export type NonUniqueCasusNameCondition = {
  conditionType: 'NonUniqueCasusNameCondition';
  duplicateCasusName: string;
};

export type NoteSourceObject = {
  deleted: boolean;
  noteId: string;
};

export type ObligationSourceObject = {
  obligationId: string;
};

export type OfficeLocationResponse = {
  active: boolean;
  activeFrom?: string;
  activeTo?: string;
  canDelete: boolean;
  id: string;
  legacyReference?: number;
  name: string;
};

export type PageCasusGroup = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemCasusGroup>;
};

export type PageCasusSearchItem = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemCasusSearchItem>;
};

export type PageCasusTemplateOverviewResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemCasusTemplateOverviewResponse>;
};

export type PageEventInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemEventInResponse>;
};

export type PageItemCasusGroup = {
  cursor: string;
  item: CasusGroup;
};

export type PageItemCasusSearchItem = {
  cursor: string;
  item: CasusSearchItem;
};

export type PageItemCasusTemplateOverviewResponse = {
  cursor: string;
  item: CasusTemplateOverviewResponse;
};

export type PageItemEventInResponse = {
  cursor: string;
  item: EventInResponse;
};

export type PageItemMinimalCasusDebtorInResponse = {
  cursor: string;
  item: MinimalCasusDebtorInResponse;
};

export type PageItemMinimalCasusWithGroupInResponse = {
  cursor: string;
  item: MinimalCasusWithGroupInResponse;
};

export type PageItemMinimalClient = {
  cursor: string;
  item: MinimalClient;
};

export type PageItemMinimalContactInResponse = {
  cursor: string;
  item: MinimalContactInResponse;
};

export type PageMinimalCasusDebtorInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemMinimalCasusDebtorInResponse>;
};

export type PageMinimalCasusWithGroupInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemMinimalCasusWithGroupInResponse>;
};

export type PageMinimalClient = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemMinimalClient>;
};

export type PageMinimalContactInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemMinimalContactInResponse>;
};

export type PaginationDirection = 'FORWARD' | 'BACKWARD';

export type PaginationMode =
  | 'BROWSE'
  | 'REFRESH'
  | 'NAVIGATE_TO_ITEM'
  | 'JUMP_TO_POSITION';

export type PaymentConsumedState = 'OPEN' | 'CONSUMED';

export type PeopleResponse = {
  authorizedGroups: Array<GroupInResponse>;
  authorizedUsers: Array<UserInResponse>;
  collaborators: Array<CollaboratorInResponse>;
  responsiblePerson: UserInResponse;
};

export type PhoneNumberInResponse = {
  id: string;
  number: string;
  phoneClassificationId: string;
};

export type PrepaymentType = 'PREPAYMENT' | 'RETAINER';

export type PublishState = 'PUBLISHED' | 'UNPUBLISHED';

export type RelatedContactAddressInResponse = {
  address: AddressInResponse;
  relatedContact: MinimalContactInResponse;
  relationshipRoleIdOfRelatedContact: string;
};

export type RelatedContactEmailAddressInResponse = {
  emailAddress: EmailAddressInResponse;
  relatedContact: MinimalContactInResponse;
  relationshipRoleIdOfRelatedContact: string;
};

export type ResponsiblePersonUpdateParams = {
  responsiblePersonUserId: string;
};

export type SearchCasusDebtorsResponse = {
  casusDebtors: PageMinimalCasusDebtorInResponse;
};

export type SearchCasusResponse = {
  casus: PageCasusSearchItem;
};

export type SearchClientsAndCasusResponse = {
  casusWithClient: PageMinimalCasusWithGroupInResponse;
  sortedGroups: Array<ClientWithGroups>;
};

export type SearchClientsResponse = {
  clients: PageMinimalClient;
};

export type SearchContactResponse = {
  contacts: PageMinimalContactInResponse;
};

export type SetAlertMessageParams = {
  alertMessage?: string;
};

export type SetCasusFlagsParams = {
  active: boolean;
  internal: boolean;
};

export type SetCasusGroupParams = {
  casusGroupId?: string;
};

export type SetCharacteristicsParams = {
  characteristics: CasusCharacteristicsInParams;
};

export type SetClientParams = {
  client: ClientReference;
};

export type SetCorrespondenceLocaleParams = {
  correspondenceLocale: string;
};

export type SetCurrencyParams = {
  currency: string;
};

export type SetDescriptionParams = {
  description?: string;
};

export type SetHourlyRateSettingsParams = {
  casusSettings?: HourlyRateSettings;
  userOrGroupSettings: Array<UserOrGroupHourlyRateSettingsParams>;
};

export type SetInvoiceSettingsParams = {
  defaultPercentageFeeAsDecimal?: string;
  exportable: boolean;
  invoiceable: boolean;
  signatoryUserIds: Array<string>;
  templateFileId?: string;
  vatNumberId?: string;
};

export type SetLockStateParams = {
  locked: boolean;
};

export type SetOfficeLocationParams = {
  officeLocationId?: string;
};

export type SetSpecialityFieldParams = {
  specialityFieldId?: string;
};

export type SetStartDateParams = {
  startDate: string;
};

export type SettingsResponse = {
  active: boolean;
  alertMessage?: string;
  casusArchivingWarnings: CasusArchivingWarnings;
  characteristics: CasusCharacteristicsInResponse;
  currency: string;
  currencyChangeable: boolean;
  hourlyRateSettings?: HourlyRateSettings;
  internal: boolean;
  invoiceSettings: InvoiceSettings;
  latestCasusArchivingLog?: CasusArchivingLog;
  locale: string;
  locked: boolean;
  officeLocationId?: string;
  permissions: CasusSettingsPermissions;
  readonly: boolean;
  specialityFieldId?: string;
  startDate: string;
  state: CasusState;
  userOrGroupHourlyRateSettings: Array<UserOrGroupHourlyRateSettings>;
};

export type SortOrder = 'ASC' | 'DESC';

export type SourceObject = {};

export type SpecialityFieldCreatedResponse = {
  id: string;
};

export type SpecialityFieldResponse = {
  active: boolean;
  activeFrom?: string;
  activeTo?: string;
  canDelete: boolean;
  id: string;
  translations: Array<SpecialityFieldTranslationResponse>;
};

export type SpecialityFieldTranslationParams = {
  locale: string;
  name: string;
};

export type SpecialityFieldTranslationResponse = {
  id: string;
  locale: string;
  name: string;
};

export type TaskSourceObject = {
  taskId: string;
};

export type TaxRate = {
  ratioAsDecimal: string;
};

export type TemplateFile = {
  displayName: string;
  locale?: string;
  templateFileId: string;
};

export type TranslatedCasusArchivingLocation = {
  active: boolean;
  id: string;
  translatedName: string;
};

export type TranslatedCollaboratorRole = {
  active: boolean;
  id: string;
  translatedName: string;
};

export type TranslatedInvolvedPartyRole = {
  active: boolean;
  id: string;
  translatedName: string;
};

export type TranslatedOfficeLocation = {
  active: boolean;
  id: string;
  translatedName: string;
};

export type TranslatedSpecialityField = {
  active: boolean;
  id: string;
  translatedName: string;
};

export type UpdateCasusArchivingLocationParams = {
  activeFrom?: string;
  activeTo?: string;
  name: string;
};

export type UpdateOfficeLocationParams = {
  activeFrom?: string;
  activeTo?: string;
  legacyReference?: number;
  name: string;
};

export type UpdateSpecialityFieldParams = {
  activeFrom?: string;
  activeTo?: string;
  translations: Array<SpecialityFieldTranslationParams>;
};

export type UserInResponse = {
  active: boolean;
  displayName: string;
  id: string;
  initialLetters: string;
};

export type UserOrGroupHourlyRateSettings = {
  active: boolean;
  billability: Billability;
  hourlyRate: Money;
  id: string;
  type: UserOrGroupType;
  userDisplayName: string;
};

export type UserOrGroupHourlyRateSettingsParams = {
  billability: Billability;
  hourlyRate: Money;
  id: string;
  type: UserOrGroupType;
};

export type UserOrGroupType = 'GROUP' | 'USER';

export type WorktimeInResponse = {
  internalDuration: number;
  mandateDuration: number;
};

export type Response_getCasusArchivingLocationsAdmin_200 =
  Array<CasusArchivingLocationResponse>;

export type Response_getInvoices_200 = Array<CasusDebtorInvoice>;

export type Response_getPayments_200 = Array<CasusDebtorPayment>;

export type Response_getOfficeLocationsAdmin_200 =
  Array<OfficeLocationResponse>;

export type Response_getSpecialityFields_200 = Array<SpecialityFieldResponse>;

export type Response_createCasusGroup_200 = string;

function sanitizeCasusArchiveDestroyParams(
  body: CasusArchiveDestroyParams
): CasusArchiveDestroyParams {
  return {
    ...body,
    summary: blankToNull(body.summary),
  };
}

function sanitizeCasusArchiveParams(
  body: CasusArchiveParams
): CasusArchiveParams {
  return {
    ...body,
    archiveReference: blankToNull(body.archiveReference),
    summary: blankToNull(body.summary),
  };
}

function sanitizeCasusArchiveUpdateParams(
  body: CasusArchiveUpdateParams
): CasusArchiveUpdateParams {
  return {
    ...body,
    archiveReference: blankToNull(body.archiveReference),
    summary: blankToNull(body.summary),
  };
}

function sanitizeCasusCharacteristicsInParams(
  body: CasusCharacteristicsInParams
): CasusCharacteristicsInParams {
  return {
    ...body,
    description: blankToNull(body.description),
    fileNumber: blankToNull(body.fileNumber),
    procedureNumber: blankToNull(body.procedureNumber),
  };
}

function sanitizeCasusCreateCharacteristicsParams(
  body: CasusCreateCharacteristicsParams
): CasusCreateCharacteristicsParams {
  return {
    ...body,
    description: blankToNull(body.description),
    fileNumber: blankToNull(body.fileNumber),
    procedureNumber: blankToNull(body.procedureNumber),
  };
}

function sanitizeCasusUnarchiveParams(
  body: CasusUnarchiveParams
): CasusUnarchiveParams {
  return {
    ...body,
    summary: blankToNull(body.summary),
  };
}

function sanitizeInvolvedPartyStoringParams(
  body: InvolvedPartyStoringParams
): InvolvedPartyStoringParams {
  return {
    ...body,
    referenceNumber: blankToNull(body.referenceNumber),
  };
}

function sanitizeSetAlertMessageParams(
  body: SetAlertMessageParams
): SetAlertMessageParams {
  return {
    ...body,
    alertMessage: blankToNull(body.alertMessage),
  };
}

function sanitizeSetDescriptionParams(
  body: SetDescriptionParams
): SetDescriptionParams {
  return {
    ...body,
    description: blankToNull(body.description),
  };
}

function sanitizeCreateCasusParams(body: CreateCasusParams): CreateCasusParams {
  return {
    ...body,
    characteristics: body.characteristics
      ? sanitizeCasusCreateCharacteristicsParams(body.characteristics)
      : body.characteristics,
  };
}

function sanitizeSetCharacteristicsParams(
  body: SetCharacteristicsParams
): SetCharacteristicsParams {
  return {
    ...body,
    characteristics: body.characteristics
      ? sanitizeCasusCharacteristicsInParams(body.characteristics)
      : body.characteristics,
  };
}

export interface ResponseWithBody<S extends number, T> extends Response {
  status: S;
  body: T;
}

export type QueryParameters = {[param: string]: any};

export interface CommonRequestOptions {
  $queryParameters?: QueryParameters;
  $domain?: string;
  $path?: string | ((path: string) => string);
  $progressCallback?: (arg0: ProgressEvent) => void;
  $retries?: number;
  $timeout?: number;
  $deadline?: number;
  $signal?: AbortSignal;
}

type SuperAgentError = {status: number};
const isSuperAgentError = (error: unknown): error is SuperAgentError =>
  Boolean((error as SuperAgentError).status);

/*
 *
 * @class Backend
 * @param {(string)} [domainOrOptions] - The project domain.
 */
export class Backend {
  private domain: string = '';
  private onUnauthorizedHandler?: CallbackHandler;
  private errorHandlers: CallbackHandler[] = [];
  private requestHeadersHandler?: RequestHeadersHandler;
  private configureAgentHandler?: ConfigureAgentHandler;
  private configureRequestHandler?: ConfigureRequestHandler;

  public constructor(domain?: string, onUnauthorizedHandler?: CallbackHandler) {
    if (domain) {
      this.domain = domain;
    }

    if (onUnauthorizedHandler) {
      this.onUnauthorizedHandler = onUnauthorizedHandler;
    }

    this.searchClientsAndCasusURL = this.searchClientsAndCasusURL.bind(this);
    this.searchClientsAndCasus = this.searchClientsAndCasus.bind(this);
    this.createCasusURL = this.createCasusURL.bind(this);
    this.createCasus = this.createCasus.bind(this);
    this.getCasusArchivingLocationsAdminURL =
      this.getCasusArchivingLocationsAdminURL.bind(this);
    this.getCasusArchivingLocationsAdmin =
      this.getCasusArchivingLocationsAdmin.bind(this);
    this.createCasusArchivingLocationURL =
      this.createCasusArchivingLocationURL.bind(this);
    this.createCasusArchivingLocation =
      this.createCasusArchivingLocation.bind(this);
    this.deleteCasusArchivingLocationURL =
      this.deleteCasusArchivingLocationURL.bind(this);
    this.deleteCasusArchivingLocation =
      this.deleteCasusArchivingLocation.bind(this);
    this.updateCasusArchivingLocationURL =
      this.updateCasusArchivingLocationURL.bind(this);
    this.updateCasusArchivingLocation =
      this.updateCasusArchivingLocation.bind(this);
    this.searchCasusDebtorsURL = this.searchCasusDebtorsURL.bind(this);
    this.searchCasusDebtors = this.searchCasusDebtors.bind(this);
    this.getAllCasusTemplatesURL = this.getAllCasusTemplatesURL.bind(this);
    this.getAllCasusTemplates = this.getAllCasusTemplates.bind(this);
    this.createCasusTemplateURL = this.createCasusTemplateURL.bind(this);
    this.createCasusTemplate = this.createCasusTemplate.bind(this);
    this.checkCasusTemplateNameAvailabilityURL =
      this.checkCasusTemplateNameAvailabilityURL.bind(this);
    this.checkCasusTemplateNameAvailability =
      this.checkCasusTemplateNameAvailability.bind(this);
    this.deleteCasusTemplateURL = this.deleteCasusTemplateURL.bind(this);
    this.deleteCasusTemplate = this.deleteCasusTemplate.bind(this);
    this.getCasusTemplateURL = this.getCasusTemplateURL.bind(this);
    this.getCasusTemplate = this.getCasusTemplate.bind(this);
    this.updateCasusTemplateURL = this.updateCasusTemplateURL.bind(this);
    this.updateCasusTemplate = this.updateCasusTemplate.bind(this);
    this.updateCasusTemplateAuthorizedGroupsURL =
      this.updateCasusTemplateAuthorizedGroupsURL.bind(this);
    this.updateCasusTemplateAuthorizedGroups =
      this.updateCasusTemplateAuthorizedGroups.bind(this);
    this.updateCasusTemplateFoldersURL =
      this.updateCasusTemplateFoldersURL.bind(this);
    this.updateCasusTemplateFolders =
      this.updateCasusTemplateFolders.bind(this);
    this.updateCasusTemplateHourlyRatesURL =
      this.updateCasusTemplateHourlyRatesURL.bind(this);
    this.updateCasusTemplateHourlyRates =
      this.updateCasusTemplateHourlyRates.bind(this);
    this.updateCasusTemplateInvoiceSettingsURL =
      this.updateCasusTemplateInvoiceSettingsURL.bind(this);
    this.updateCasusTemplateInvoiceSettings =
      this.updateCasusTemplateInvoiceSettings.bind(this);
    this.searchAllCasusURL = this.searchAllCasusURL.bind(this);
    this.searchAllCasus = this.searchAllCasus.bind(this);
    this.casusAssortmentURL = this.casusAssortmentURL.bind(this);
    this.casusAssortment = this.casusAssortment.bind(this);
    this.getAllCasusCockpitRecentDocumentsURL =
      this.getAllCasusCockpitRecentDocumentsURL.bind(this);
    this.getAllCasusCockpitRecentDocuments =
      this.getAllCasusCockpitRecentDocuments.bind(this);
    this.getAllCasusCockpitTimelineEventsURL =
      this.getAllCasusCockpitTimelineEventsURL.bind(this);
    this.getAllCasusCockpitTimelineEvents =
      this.getAllCasusCockpitTimelineEvents.bind(this);
    this.getAllCasusCockpitTimetrackingMonthURL =
      this.getAllCasusCockpitTimetrackingMonthURL.bind(this);
    this.getAllCasusCockpitTimetrackingMonth =
      this.getAllCasusCockpitTimetrackingMonth.bind(this);
    this.getAllCasusCockpitTimetrackingWeekURL =
      this.getAllCasusCockpitTimetrackingWeekURL.bind(this);
    this.getAllCasusCockpitTimetrackingWeek =
      this.getAllCasusCockpitTimetrackingWeek.bind(this);
    this.getAllCasusCockpitTimetrackingYearURL =
      this.getAllCasusCockpitTimetrackingYearURL.bind(this);
    this.getAllCasusCockpitTimetrackingYear =
      this.getAllCasusCockpitTimetrackingYear.bind(this);
    this.importCasusURL = this.importCasusURL.bind(this);
    this.importCasus = this.importCasus.bind(this);
    this.checkNameAvailabilityURL = this.checkNameAvailabilityURL.bind(this);
    this.checkNameAvailability = this.checkNameAvailability.bind(this);
    this.checkNameAvailabilityByClientIdURL =
      this.checkNameAvailabilityByClientIdURL.bind(this);
    this.checkNameAvailabilityByClientId =
      this.checkNameAvailabilityByClientId.bind(this);
    this.deleteCasusURL = this.deleteCasusURL.bind(this);
    this.deleteCasus = this.deleteCasus.bind(this);
    this.getCasusURL = this.getCasusURL.bind(this);
    this.getCasus = this.getCasus.bind(this);
    this.createCasusArchiveURL = this.createCasusArchiveURL.bind(this);
    this.createCasusArchive = this.createCasusArchive.bind(this);
    this.updateCasusArchiveURL = this.updateCasusArchiveURL.bind(this);
    this.updateCasusArchive = this.updateCasusArchive.bind(this);
    this.getArchivesURL = this.getArchivesURL.bind(this);
    this.getArchives = this.getArchives.bind(this);
    this.checkCasusDeletionPossibleURL =
      this.checkCasusDeletionPossibleURL.bind(this);
    this.checkCasusDeletionPossible =
      this.checkCasusDeletionPossible.bind(this);
    this.setClientURL = this.setClientURL.bind(this);
    this.setClient = this.setClient.bind(this);
    this.getSingleCasusCockpitFinanceOverviewURL =
      this.getSingleCasusCockpitFinanceOverviewURL.bind(this);
    this.getSingleCasusCockpitFinanceOverview =
      this.getSingleCasusCockpitFinanceOverview.bind(this);
    this.getSingleCasusCockpitInvolvedPeopleURL =
      this.getSingleCasusCockpitInvolvedPeopleURL.bind(this);
    this.getSingleCasusCockpitInvolvedPeople =
      this.getSingleCasusCockpitInvolvedPeople.bind(this);
    this.getSingleCasusCockpitTimelineEventsURL =
      this.getSingleCasusCockpitTimelineEventsURL.bind(this);
    this.getSingleCasusCockpitTimelineEvents =
      this.getSingleCasusCockpitTimelineEvents.bind(this);
    this.getContactsURL = this.getContactsURL.bind(this);
    this.getContacts = this.getContacts.bind(this);
    this.addCasusDebtorURL = this.addCasusDebtorURL.bind(this);
    this.addCasusDebtor = this.addCasusDebtor.bind(this);
    this.deleteCasusDebtorURL = this.deleteCasusDebtorURL.bind(this);
    this.deleteCasusDebtor = this.deleteCasusDebtor.bind(this);
    this.updateCasusDebtorURL = this.updateCasusDebtorURL.bind(this);
    this.updateCasusDebtor = this.updateCasusDebtor.bind(this);
    this.getInvoicesURL = this.getInvoicesURL.bind(this);
    this.getInvoices = this.getInvoices.bind(this);
    this.getPaymentsURL = this.getPaymentsURL.bind(this);
    this.getPayments = this.getPayments.bind(this);
    this.setCasusDebtorFractionsURL =
      this.setCasusDebtorFractionsURL.bind(this);
    this.setCasusDebtorFractions = this.setCasusDebtorFractions.bind(this);
    this.getDefaultInvoiceRecipientsURL =
      this.getDefaultInvoiceRecipientsURL.bind(this);
    this.getDefaultInvoiceRecipients =
      this.getDefaultInvoiceRecipients.bind(this);
    this.createInvolvedPartyURL = this.createInvolvedPartyURL.bind(this);
    this.createInvolvedParty = this.createInvolvedParty.bind(this);
    this.deleteInvolvedPartyURL = this.deleteInvolvedPartyURL.bind(this);
    this.deleteInvolvedParty = this.deleteInvolvedParty.bind(this);
    this.getInvolvedPartyURL = this.getInvolvedPartyURL.bind(this);
    this.getInvolvedParty = this.getInvolvedParty.bind(this);
    this.updateInvolvedPartyURL = this.updateInvolvedPartyURL.bind(this);
    this.updateInvolvedParty = this.updateInvolvedParty.bind(this);
    this.createCasusArchiveDestroyURL =
      this.createCasusArchiveDestroyURL.bind(this);
    this.createCasusArchiveDestroy = this.createCasusArchiveDestroy.bind(this);
    this.updateCasusArchiveDestroyURL =
      this.updateCasusArchiveDestroyURL.bind(this);
    this.updateCasusArchiveDestroy = this.updateCasusArchiveDestroy.bind(this);
    this.getLatestArchivingLogURL = this.getLatestArchivingLogURL.bind(this);
    this.getLatestArchivingLog = this.getLatestArchivingLog.bind(this);
    this.getPeopleURL = this.getPeopleURL.bind(this);
    this.getPeople = this.getPeople.bind(this);
    this.removeAuthorizeGroupURL = this.removeAuthorizeGroupURL.bind(this);
    this.removeAuthorizeGroup = this.removeAuthorizeGroup.bind(this);
    this.addAuthorizedGroupURL = this.addAuthorizedGroupURL.bind(this);
    this.addAuthorizedGroup = this.addAuthorizedGroup.bind(this);
    this.removeAuthorizedUserURL = this.removeAuthorizedUserURL.bind(this);
    this.removeAuthorizedUser = this.removeAuthorizedUser.bind(this);
    this.addAuthorizedUserURL = this.addAuthorizedUserURL.bind(this);
    this.addAuthorizedUser = this.addAuthorizedUser.bind(this);
    this.createCollaboratorURL = this.createCollaboratorURL.bind(this);
    this.createCollaborator = this.createCollaborator.bind(this);
    this.deleteCollaboratorURL = this.deleteCollaboratorURL.bind(this);
    this.deleteCollaborator = this.deleteCollaborator.bind(this);
    this.updateCollaboratorURL = this.updateCollaboratorURL.bind(this);
    this.updateCollaborator = this.updateCollaborator.bind(this);
    this.setResponsiblePersonURL = this.setResponsiblePersonURL.bind(this);
    this.setResponsiblePerson = this.setResponsiblePerson.bind(this);
    this.getSettingsURL = this.getSettingsURL.bind(this);
    this.getSettings = this.getSettings.bind(this);
    this.setAlertMessageURL = this.setAlertMessageURL.bind(this);
    this.setAlertMessage = this.setAlertMessage.bind(this);
    this.setCasusFlagsURL = this.setCasusFlagsURL.bind(this);
    this.setCasusFlags = this.setCasusFlags.bind(this);
    this.setCasusGroupURL = this.setCasusGroupURL.bind(this);
    this.setCasusGroup = this.setCasusGroup.bind(this);
    this.setCharacteristicsURL = this.setCharacteristicsURL.bind(this);
    this.setCharacteristics = this.setCharacteristics.bind(this);
    this.setCorrespondenceLocaleURL =
      this.setCorrespondenceLocaleURL.bind(this);
    this.setCorrespondenceLocale = this.setCorrespondenceLocale.bind(this);
    this.setCurrencyURL = this.setCurrencyURL.bind(this);
    this.setCurrency = this.setCurrency.bind(this);
    this.setDescriptionURL = this.setDescriptionURL.bind(this);
    this.setDescription = this.setDescription.bind(this);
    this.setHourlyRateSettingsURL = this.setHourlyRateSettingsURL.bind(this);
    this.setHourlyRateSettings = this.setHourlyRateSettings.bind(this);
    this.setInvoiceSettingsURL = this.setInvoiceSettingsURL.bind(this);
    this.setInvoiceSettings = this.setInvoiceSettings.bind(this);
    this.setLockStateURL = this.setLockStateURL.bind(this);
    this.setLockState = this.setLockState.bind(this);
    this.setOfficeLocationURL = this.setOfficeLocationURL.bind(this);
    this.setOfficeLocation = this.setOfficeLocation.bind(this);
    this.setSpecialityFieldURL = this.setSpecialityFieldURL.bind(this);
    this.setSpecialityField = this.setSpecialityField.bind(this);
    this.setStartDateURL = this.setStartDateURL.bind(this);
    this.setStartDate = this.setStartDate.bind(this);
    this.createCasusUnarchiveURL = this.createCasusUnarchiveURL.bind(this);
    this.createCasusUnarchive = this.createCasusUnarchive.bind(this);
    this.updateCasusUnarchiveURL = this.updateCasusUnarchiveURL.bind(this);
    this.updateCasusUnarchive = this.updateCasusUnarchive.bind(this);
    this.searchClientsURL = this.searchClientsURL.bind(this);
    this.searchClients = this.searchClients.bind(this);
    this.searchContactsURL = this.searchContactsURL.bind(this);
    this.searchContacts = this.searchContacts.bind(this);
    this.getRelatedAddressesURL = this.getRelatedAddressesURL.bind(this);
    this.getRelatedAddresses = this.getRelatedAddresses.bind(this);
    this.getRelatedEmailAddressesURL =
      this.getRelatedEmailAddressesURL.bind(this);
    this.getRelatedEmailAddresses = this.getRelatedEmailAddresses.bind(this);
    this.deleteCasusGroupURL = this.deleteCasusGroupURL.bind(this);
    this.deleteCasusGroup = this.deleteCasusGroup.bind(this);
    this.updateCasusGroupURL = this.updateCasusGroupURL.bind(this);
    this.updateCasusGroup = this.updateCasusGroup.bind(this);
    this.getCasusMasterDataURL = this.getCasusMasterDataURL.bind(this);
    this.getCasusMasterData = this.getCasusMasterData.bind(this);
    this.getOfficeLocationsAdminURL =
      this.getOfficeLocationsAdminURL.bind(this);
    this.getOfficeLocationsAdmin = this.getOfficeLocationsAdmin.bind(this);
    this.createOfficeLocationURL = this.createOfficeLocationURL.bind(this);
    this.createOfficeLocation = this.createOfficeLocation.bind(this);
    this.deleteOfficeLocationURL = this.deleteOfficeLocationURL.bind(this);
    this.deleteOfficeLocation = this.deleteOfficeLocation.bind(this);
    this.updateOfficeLocationURL = this.updateOfficeLocationURL.bind(this);
    this.updateOfficeLocation = this.updateOfficeLocation.bind(this);
    this.getSpecialityFieldsURL = this.getSpecialityFieldsURL.bind(this);
    this.getSpecialityFields = this.getSpecialityFields.bind(this);
    this.createSpecialityFieldURL = this.createSpecialityFieldURL.bind(this);
    this.createSpecialityField = this.createSpecialityField.bind(this);
    this.deleteSpecialityFieldURL = this.deleteSpecialityFieldURL.bind(this);
    this.deleteSpecialityField = this.deleteSpecialityField.bind(this);
    this.updateSpecialityFieldURL = this.updateSpecialityFieldURL.bind(this);
    this.updateSpecialityField = this.updateSpecialityField.bind(this);
    this.setFavoriteURL = this.setFavoriteURL.bind(this);
    this.setFavorite = this.setFavorite.bind(this);
    this.setMostRecentlyUsedCasusURL =
      this.setMostRecentlyUsedCasusURL.bind(this);
    this.setMostRecentlyUsedCasus = this.setMostRecentlyUsedCasus.bind(this);
    this.searchCasusGroupsURL = this.searchCasusGroupsURL.bind(this);
    this.searchCasusGroups = this.searchCasusGroups.bind(this);
    this.createCasusGroupURL = this.createCasusGroupURL.bind(this);
    this.createCasusGroup = this.createCasusGroup.bind(this);
    this.validateCasusGroupNameURL = this.validateCasusGroupNameURL.bind(this);
    this.validateCasusGroupName = this.validateCasusGroupName.bind(this);
  }

  public getDomain(): string {
    return this.domain;
  }

  public addErrorHandler(handler: CallbackHandler): void {
    this.errorHandlers.push(handler);
  }

  public setRequestHeadersHandler(handler: RequestHeadersHandler): void {
    this.requestHeadersHandler = handler;
  }

  public setConfigureAgentHandler(handler: ConfigureAgentHandler): void {
    this.configureAgentHandler = handler;
  }

  public setConfigureRequestHandler(handler: ConfigureRequestHandler): void {
    this.configureRequestHandler = handler;
  }

  public request(
    method: string,
    url: string,
    body: any,
    headers: RequestHeaders,
    form: any,
    reject: CallbackHandler,
    resolve: CallbackHandler,
    progress: CallbackHandler,
    opts: CommonRequestOptions,
    signal?: AbortSignal,
    skipRedirectOnUnauthorized = false
  ): void {
    const agent = this.configureAgentHandler
      ? this.configureAgentHandler(superAgentRequest)
      : superAgentRequest;

    if (process.env.NODE_ENV === 'test') {
      throw new Error(
        'Calling endpoints in test environment should be avoided, this endpoint needs to be mocked: ' +
          url +
          ' [' +
          method +
          ']'
      );
    }

    let request = agent(method, url);
    if (this.configureRequestHandler) {
      request = this.configureRequestHandler(request);
    }

    let processedHeaders = headers;
    if (this.requestHeadersHandler) {
      processedHeaders = this.requestHeadersHandler({...headers});
    }

    if (body) {
      request.send(body);

      if (typeof body === 'object' && !(body.constructor.name === 'Buffer')) {
        processedHeaders['Content-Type'] = 'application/json';
      }
    }

    if (Object.keys(form).length > 0) {
      // do not explicitly set the the content type
      // https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post#39281156
      delete processedHeaders['content-type'];
      const formData = new FormData();

      Object.keys(form).forEach((key: string) =>
        formData.append(key, form[key])
      );
      request.send(formData);
    }

    request.set(processedHeaders);

    if (opts.$retries && opts.$retries > 0) {
      request.retry(opts.$retries);
    }

    if (
      (opts.$timeout && opts.$timeout > 0) ||
      (opts.$deadline && opts.$deadline > 0)
    ) {
      request.timeout({deadline: opts.$deadline, response: opts.$timeout});
    }

    request
      .on('progress', progress)
      .on('abort', () => reject('Aborted'))
      .ok((res) => res.status <= 400)
      .end((error, response) => {
        // an error will also be emitted for a 4xx and 5xx status code
        // the error object will then have error.status and error.response fields
        // see superagent error handling: https://github.com/visionmedia/superagent/blob/master/docs/index.md#error-handling
        if (error) {
          reject(error);
          if (!skipRedirectOnUnauthorized) {
            this.onUnauthorizedHandler?.(error);
          }
          this.errorHandlers.forEach((handler) => handler(error));
        } else {
          resolve(response);
        }
      });

    if (signal) {
      signal.addEventListener('abort', () => {
        request.abort();
      });
    }
  }

  private static convertParameterCollectionFormat<T>(
    param: T,
    collectionFormat: string | undefined
  ): T | string {
    if (Array.isArray(param) && param.length >= 2) {
      switch (collectionFormat) {
        case 'csv':
          return param.join(',');
        case 'ssv':
          return param.join(' ');
        case 'tsv':
          return param.join('\t');
        case 'pipes':
          return param.join('|');
        default:
          return param;
      }
    }

    return param;
  }

  /*
   *
   * @method
   * @name Backend#searchClientsAndCasus
   * @param {array} clientIds -
   * @param {boolean} exactSearch -
   * @param {array} ids -
   * @param {string} invoiceableFilter -
   * @param {string} lockedFilter -
   * @param {string} officeLocationId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} protectedFilter -
   * @param {string} query -
   * @param {string} responsiblePersonOrCollaboratorUserId -
   * @param {string} specialityFieldId -
   * @param {array} stateFilters -
   * @param {string} type -
   */
  public async searchClientsAndCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientIds?: Array<string>;
      exactSearch: boolean;
      ids?: Array<string>;
      invoiceableFilter: 'ALL' | 'INVOICEABLE' | 'NOT_INVOICEABLE';
      lockedFilter: 'ALL' | 'LOCKED' | 'UNLOCKED';
      officeLocationId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      protectedFilter: 'ALL' | 'PROTECTED' | 'PUBLIC';
      query?: string;
      responsiblePersonOrCollaboratorUserId?: string;
      specialityFieldId?: string;
      stateFilters: Array<'ACTIVE' | 'INACTIVE' | 'ARCHIVED'>;
      type: 'ALL' | 'INTERNAL' | 'NOT_INTERNAL';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchClientsAndCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchClientsAndCasusResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, SearchClientsAndCasusResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['exactSearch'] === 'undefined') {
        reject(new Error('Missing required  parameter: exactSearch'));
        return;
      }

      if (typeof parameters['invoiceableFilter'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceableFilter'));
        return;
      }

      if (typeof parameters['lockedFilter'] === 'undefined') {
        reject(new Error('Missing required  parameter: lockedFilter'));
        return;
      }

      if (typeof parameters['paginationDirection'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationDirection'));
        return;
      }

      if (typeof parameters['paginationMode'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationMode'));
        return;
      }

      if (typeof parameters['protectedFilter'] === 'undefined') {
        reject(new Error('Missing required  parameter: protectedFilter'));
        return;
      }

      if (typeof parameters['stateFilters'] === 'undefined') {
        reject(new Error('Missing required  parameter: stateFilters'));
        return;
      }

      if (typeof parameters['type'] === 'undefined') {
        reject(new Error('Missing required  parameter: type'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.searchClientsAndCasusURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchClientsAndCasusResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchClientsAndCasusResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchClientsAndCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        SearchClientsAndCasusResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public searchClientsAndCasusURL(
    parameters: {
      clientIds?: Array<string>;
      exactSearch: boolean;
      ids?: Array<string>;
      invoiceableFilter: 'ALL' | 'INVOICEABLE' | 'NOT_INVOICEABLE';
      lockedFilter: 'ALL' | 'LOCKED' | 'UNLOCKED';
      officeLocationId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      protectedFilter: 'ALL' | 'PROTECTED' | 'PUBLIC';
      query?: string;
      responsiblePersonOrCollaboratorUserId?: string;
      specialityFieldId?: string;
      stateFilters: Array<'ACTIVE' | 'INACTIVE' | 'ARCHIVED'>;
      type: 'ALL' | 'INTERNAL' | 'NOT_INTERNAL';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['clientIds'] !== 'undefined') {
      queryParameters['clientIds'] = Backend.convertParameterCollectionFormat(
        parameters['clientIds'],
        ''
      );
    }

    if (typeof parameters['exactSearch'] !== 'undefined') {
      queryParameters['exactSearch'] = Backend.convertParameterCollectionFormat(
        parameters['exactSearch'],
        ''
      );
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['invoiceableFilter'] !== 'undefined') {
      queryParameters['invoiceableFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceableFilter'],
          ''
        );
    }

    if (typeof parameters['lockedFilter'] !== 'undefined') {
      queryParameters['lockedFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['lockedFilter'],
          ''
        );
    }

    if (typeof parameters['officeLocationId'] !== 'undefined') {
      queryParameters['officeLocationId'] =
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationId'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['protectedFilter'] !== 'undefined') {
      queryParameters['protectedFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['protectedFilter'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (
      typeof parameters['responsiblePersonOrCollaboratorUserId'] !== 'undefined'
    ) {
      queryParameters['responsiblePersonOrCollaboratorUserId'] =
        Backend.convertParameterCollectionFormat(
          parameters['responsiblePersonOrCollaboratorUserId'],
          ''
        );
    }

    if (typeof parameters['specialityFieldId'] !== 'undefined') {
      queryParameters['specialityFieldId'] =
        Backend.convertParameterCollectionFormat(
          parameters['specialityFieldId'],
          ''
        );
    }

    if (typeof parameters['stateFilters'] !== 'undefined') {
      queryParameters['stateFilters'] =
        Backend.convertParameterCollectionFormat(
          parameters['stateFilters'],
          ''
        );
    }

    if (typeof parameters['type'] !== 'undefined') {
      queryParameters['type'] = Backend.convertParameterCollectionFormat(
        parameters['type'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasus
   * @param {} body -
   * @param {boolean} checkForDuplicateClientContacts -
   */
  public async createCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateCasusParams;
      checkForDuplicateClientContacts: boolean;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreateCasusResponse>
          | ResponseWithBody<400, CasusCreationCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreateCasusResponse>
      | ResponseWithBody<400, CasusCreationCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
        body = sanitizeCreateCasusParams(body);
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (
        typeof parameters['checkForDuplicateClientContacts'] === 'undefined'
      ) {
        reject(
          new Error(
            'Missing required  parameter: checkForDuplicateClientContacts'
          )
        );
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createCasusURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreateCasusResponse>
      | ResponseWithBody<400, CasusCreationCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreateCasusResponse>
          | ResponseWithBody<400, CasusCreationCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreateCasusResponse>
        | ResponseWithBody<400, CasusCreationCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusURL(
    parameters: {
      body: CreateCasusParams;
      checkForDuplicateClientContacts: boolean;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['checkForDuplicateClientContacts'] !== 'undefined') {
      queryParameters['checkForDuplicateClientContacts'] =
        Backend.convertParameterCollectionFormat(
          parameters['checkForDuplicateClientContacts'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasusArchivingLocationsAdmin
   */
  public async getCasusArchivingLocationsAdmin({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getCasusArchivingLocationsAdmin_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_getCasusArchivingLocationsAdmin_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getCasusArchivingLocationsAdmin_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getCasusArchivingLocationsAdminURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      Response_getCasusArchivingLocationsAdmin_200
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getCasusArchivingLocationsAdmin_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getCasusArchivingLocationsAdmin_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getCasusArchivingLocationsAdmin_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusArchivingLocationsAdminURL(
    parameters: CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-archiving-locations/admin/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasusArchivingLocation
   * @param {} body -
   */
  public async createCasusArchivingLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateCasusArchivingLocationParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreatedCasusArchivingLocationResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, CreatedCasusArchivingLocationResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CreatedCasusArchivingLocationResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createCasusArchivingLocationURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreatedCasusArchivingLocationResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreatedCasusArchivingLocationResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreatedCasusArchivingLocationResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CreatedCasusArchivingLocationResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusArchivingLocationURL(
    parameters: {
      body: CreateCasusArchivingLocationParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-archiving-locations/admin/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteCasusArchivingLocation
   * @param {string} casusArchivingLocationId -
   */
  public async deleteCasusArchivingLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusArchivingLocationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusArchivingLocationId'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: casusArchivingLocationId')
          );
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteCasusArchivingLocationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteCasusArchivingLocationURL(
    parameters: {
      casusArchivingLocationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus-archiving-locations/admin/{casusArchivingLocationId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusArchivingLocationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusArchivingLocationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusArchivingLocation
   * @param {} body -
   * @param {string} casusArchivingLocationId -
   */
  public async updateCasusArchivingLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UpdateCasusArchivingLocationParams;
      casusArchivingLocationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusArchivingLocationId'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: casusArchivingLocationId')
          );
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusArchivingLocationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusArchivingLocationURL(
    parameters: {
      body: UpdateCasusArchivingLocationParams;
      casusArchivingLocationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus-archiving-locations/admin/{casusArchivingLocationId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusArchivingLocationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusArchivingLocationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchCasusDebtors
   * @param {string} casusId -
   * @param {boolean} excludeReadonlyCasus -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchCasusDebtors({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId?: string;
      excludeReadonlyCasus?: boolean;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchCasusDebtorsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchCasusDebtorsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchCasusDebtorsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchCasusDebtorsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchCasusDebtorsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchCasusDebtorsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchCasusDebtorsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchCasusDebtorsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchCasusDebtorsURL(
    parameters: {
      casusId?: string;
      excludeReadonlyCasus?: boolean;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-debtors/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['excludeReadonlyCasus'] !== 'undefined') {
      queryParameters['excludeReadonlyCasus'] =
        Backend.convertParameterCollectionFormat(
          parameters['excludeReadonlyCasus'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllCasusTemplates
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} publishState -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getAllCasusTemplates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      publishState?: 'PUBLISHED' | 'UNPUBLISHED';
      query?: string;
      sortFields: Array<
        'NAME' | 'UNIQUE_IDENTIFIER' | 'PUBLISHED' | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PageCasusTemplateOverviewResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PageCasusTemplateOverviewResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, PageCasusTemplateOverviewResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paginationDirection'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationDirection'));
        return;
      }

      if (typeof parameters['paginationMode'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationMode'));
        return;
      }

      if (typeof parameters['sortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: sortFields'));
        return;
      }

      if (typeof parameters['sortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: sortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllCasusTemplatesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PageCasusTemplateOverviewResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PageCasusTemplateOverviewResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PageCasusTemplateOverviewResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        PageCasusTemplateOverviewResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllCasusTemplatesURL(
    parameters: {
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      publishState?: 'PUBLISHED' | 'UNPUBLISHED';
      query?: string;
      sortFields: Array<
        'NAME' | 'UNIQUE_IDENTIFIER' | 'PUBLISHED' | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['publishState'] !== 'undefined') {
      queryParameters['publishState'] =
        Backend.convertParameterCollectionFormat(
          parameters['publishState'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasusTemplate
   * @param {} body -
   */
  public async createCasusTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusTemplateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CasusTemplateCreatedResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CasusTemplateCreatedResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CasusTemplateCreatedResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createCasusTemplateURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CasusTemplateCreatedResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CasusTemplateCreatedResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CasusTemplateCreatedResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CasusTemplateCreatedResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusTemplateURL(
    parameters: {
      body: CasusTemplateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkCasusTemplateNameAvailability
   * @param {string} casusTemplateName -
   */
  public async checkCasusTemplateNameAvailability({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusTemplateName: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CheckNameAvailabilityResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CheckNameAvailabilityResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CheckNameAvailabilityResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusTemplateName'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusTemplateName'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.checkCasusTemplateNameAvailabilityURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CheckNameAvailabilityResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CheckNameAvailabilityResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CheckNameAvailabilityResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CheckNameAvailabilityResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public checkCasusTemplateNameAvailabilityURL(
    parameters: {
      casusTemplateName: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/name-availability/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusTemplateName'] !== 'undefined') {
      queryParameters['casusTemplateName'] =
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateName'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteCasusTemplate
   * @param {string} casusTemplateId -
   */
  public async deleteCasusTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteCasusTemplateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteCasusTemplateURL(
    parameters: {
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/{casusTemplateId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasusTemplate
   * @param {string} casusTemplateId -
   */
  public async getCasusTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CasusTemplateResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CasusTemplateResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CasusTemplateResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getCasusTemplateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CasusTemplateResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CasusTemplateResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CasusTemplateResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CasusTemplateResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusTemplateURL(
    parameters: {
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/{casusTemplateId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusTemplate
   * @param {} body -
   * @param {string} casusTemplateId -
   */
  public async updateCasusTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusTemplateParams;
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusTemplateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusTemplateURL(
    parameters: {
      body: CasusTemplateParams;
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/{casusTemplateId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusTemplateAuthorizedGroups
   * @param {} body -
   * @param {string} casusTemplateId -
   */
  public async updateCasusTemplateAuthorizedGroups({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusTemplateAuthorizedGroups;
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusTemplateAuthorizedGroupsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusTemplateAuthorizedGroupsURL(
    parameters: {
      body: CasusTemplateAuthorizedGroups;
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus-templates/{casusTemplateId}/authorized-groups/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusTemplateFolders
   * @param {} body -
   * @param {string} casusTemplateId -
   */
  public async updateCasusTemplateFolders({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusTemplateFolder;
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusTemplateFoldersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusTemplateFoldersURL(
    parameters: {
      body: CasusTemplateFolder;
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus-templates/{casusTemplateId}/folders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusTemplateHourlyRates
   * @param {} body -
   * @param {string} casusTemplateId -
   */
  public async updateCasusTemplateHourlyRates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusTemplateHourlyRateParams;
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusTemplateHourlyRatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusTemplateHourlyRatesURL(
    parameters: {
      body: CasusTemplateHourlyRateParams;
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus-templates/{casusTemplateId}/hourly-rates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusTemplateInvoiceSettings
   * @param {} body -
   * @param {string} casusTemplateId -
   */
  public async updateCasusTemplateInvoiceSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusTemplateInvoiceParams;
      casusTemplateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusTemplateId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusTemplateId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusTemplateInvoiceSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusTemplateInvoiceSettingsURL(
    parameters: {
      body: CasusTemplateInvoiceParams;
      casusTemplateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus-templates/{casusTemplateId}/invoice-settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusTemplateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusTemplateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchAllCasus
   * @param {array} clientIds -
   * @param {boolean} exactSearch -
   * @param {array} ids -
   * @param {string} invoiceableFilter -
   * @param {string} lockedFilter -
   * @param {string} officeLocationId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} protectedFilter -
   * @param {string} query -
   * @param {string} responsiblePersonOrCollaboratorUserId -
   * @param {string} specialityFieldId -
   * @param {array} stateFilters -
   * @param {string} type -
   */
  public async searchAllCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientIds?: Array<string>;
      exactSearch: boolean;
      ids?: Array<string>;
      invoiceableFilter: 'ALL' | 'INVOICEABLE' | 'NOT_INVOICEABLE';
      lockedFilter: 'ALL' | 'LOCKED' | 'UNLOCKED';
      officeLocationId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      protectedFilter: 'ALL' | 'PROTECTED' | 'PUBLIC';
      query?: string;
      responsiblePersonOrCollaboratorUserId?: string;
      specialityFieldId?: string;
      stateFilters: Array<'ACTIVE' | 'INACTIVE' | 'ARCHIVED'>;
      type: 'ALL' | 'INTERNAL' | 'NOT_INTERNAL';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchCasusResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchCasusResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['exactSearch'] === 'undefined') {
          reject(new Error('Missing required  parameter: exactSearch'));
          return;
        }

        if (typeof parameters['invoiceableFilter'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceableFilter'));
          return;
        }

        if (typeof parameters['lockedFilter'] === 'undefined') {
          reject(new Error('Missing required  parameter: lockedFilter'));
          return;
        }

        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['protectedFilter'] === 'undefined') {
          reject(new Error('Missing required  parameter: protectedFilter'));
          return;
        }

        if (typeof parameters['stateFilters'] === 'undefined') {
          reject(new Error('Missing required  parameter: stateFilters'));
          return;
        }

        if (typeof parameters['type'] === 'undefined') {
          reject(new Error('Missing required  parameter: type'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchAllCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchCasusResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchCasusResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchCasusResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchAllCasusURL(
    parameters: {
      clientIds?: Array<string>;
      exactSearch: boolean;
      ids?: Array<string>;
      invoiceableFilter: 'ALL' | 'INVOICEABLE' | 'NOT_INVOICEABLE';
      lockedFilter: 'ALL' | 'LOCKED' | 'UNLOCKED';
      officeLocationId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      protectedFilter: 'ALL' | 'PROTECTED' | 'PUBLIC';
      query?: string;
      responsiblePersonOrCollaboratorUserId?: string;
      specialityFieldId?: string;
      stateFilters: Array<'ACTIVE' | 'INACTIVE' | 'ARCHIVED'>;
      type: 'ALL' | 'INTERNAL' | 'NOT_INTERNAL';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/all/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['clientIds'] !== 'undefined') {
      queryParameters['clientIds'] = Backend.convertParameterCollectionFormat(
        parameters['clientIds'],
        ''
      );
    }

    if (typeof parameters['exactSearch'] !== 'undefined') {
      queryParameters['exactSearch'] = Backend.convertParameterCollectionFormat(
        parameters['exactSearch'],
        ''
      );
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['invoiceableFilter'] !== 'undefined') {
      queryParameters['invoiceableFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceableFilter'],
          ''
        );
    }

    if (typeof parameters['lockedFilter'] !== 'undefined') {
      queryParameters['lockedFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['lockedFilter'],
          ''
        );
    }

    if (typeof parameters['officeLocationId'] !== 'undefined') {
      queryParameters['officeLocationId'] =
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationId'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['protectedFilter'] !== 'undefined') {
      queryParameters['protectedFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['protectedFilter'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (
      typeof parameters['responsiblePersonOrCollaboratorUserId'] !== 'undefined'
    ) {
      queryParameters['responsiblePersonOrCollaboratorUserId'] =
        Backend.convertParameterCollectionFormat(
          parameters['responsiblePersonOrCollaboratorUserId'],
          ''
        );
    }

    if (typeof parameters['specialityFieldId'] !== 'undefined') {
      queryParameters['specialityFieldId'] =
        Backend.convertParameterCollectionFormat(
          parameters['specialityFieldId'],
          ''
        );
    }

    if (typeof parameters['stateFilters'] !== 'undefined') {
      queryParameters['stateFilters'] =
        Backend.convertParameterCollectionFormat(
          parameters['stateFilters'],
          ''
        );
    }

    if (typeof parameters['type'] !== 'undefined') {
      queryParameters['type'] = Backend.convertParameterCollectionFormat(
        parameters['type'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#casusAssortment
   */
  public async casusAssortment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CasusAssortmentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CasusAssortmentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CasusAssortmentResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.casusAssortmentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CasusAssortmentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CasusAssortmentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CasusAssortmentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CasusAssortmentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public casusAssortmentURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/assortment/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllCasusCockpitRecentDocuments
   */
  public async getAllCasusCockpitRecentDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitRecentDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitRecentDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitRecentDocumentsResponse>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllCasusCockpitRecentDocumentsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitRecentDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitRecentDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitRecentDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitRecentDocumentsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllCasusCockpitRecentDocumentsURL(
    parameters: CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/cockpit/recent-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllCasusCockpitTimelineEvents
   * @param {array} eventContexts -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   */
  public async getAllCasusCockpitTimelineEvents({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      eventContexts: Array<'DOCUMENT' | 'OBLIGATION' | 'TASK' | 'NOTE'>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitTimelineEventsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitTimelineEventsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitTimelineEventsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['eventContexts'] === 'undefined') {
        reject(new Error('Missing required  parameter: eventContexts'));
        return;
      }

      if (typeof parameters['paginationDirection'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationDirection'));
        return;
      }

      if (typeof parameters['paginationMode'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationMode'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllCasusCockpitTimelineEventsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitTimelineEventsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitTimelineEventsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitTimelineEventsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitTimelineEventsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllCasusCockpitTimelineEventsURL(
    parameters: {
      eventContexts: Array<'DOCUMENT' | 'OBLIGATION' | 'TASK' | 'NOTE'>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/cockpit/timeline-events/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['eventContexts'] !== 'undefined') {
      queryParameters['eventContexts'] =
        Backend.convertParameterCollectionFormat(
          parameters['eventContexts'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllCasusCockpitTimetrackingMonth
   * @param {string} referenceDay -
   */
  public async getAllCasusCockpitTimetrackingMonth({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      referenceDay: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitTimetrackingMonthResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitTimetrackingMonthResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitTimetrackingMonthResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['referenceDay'] === 'undefined') {
        reject(new Error('Missing required  parameter: referenceDay'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllCasusCockpitTimetrackingMonthURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitTimetrackingMonthResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitTimetrackingMonthResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitTimetrackingMonthResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitTimetrackingMonthResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllCasusCockpitTimetrackingMonthURL(
    parameters: {
      referenceDay: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/cockpit/timetracking-month/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['referenceDay'] !== 'undefined') {
      queryParameters['referenceDay'] =
        Backend.convertParameterCollectionFormat(
          parameters['referenceDay'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllCasusCockpitTimetrackingWeek
   * @param {string} referenceDay -
   */
  public async getAllCasusCockpitTimetrackingWeek({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      referenceDay: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitTimetrackingWeekResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitTimetrackingWeekResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitTimetrackingWeekResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['referenceDay'] === 'undefined') {
        reject(new Error('Missing required  parameter: referenceDay'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllCasusCockpitTimetrackingWeekURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitTimetrackingWeekResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitTimetrackingWeekResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitTimetrackingWeekResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitTimetrackingWeekResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllCasusCockpitTimetrackingWeekURL(
    parameters: {
      referenceDay: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/cockpit/timetracking-week/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['referenceDay'] !== 'undefined') {
      queryParameters['referenceDay'] =
        Backend.convertParameterCollectionFormat(
          parameters['referenceDay'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllCasusCockpitTimetrackingYear
   * @param {string} referenceDay -
   */
  public async getAllCasusCockpitTimetrackingYear({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      referenceDay: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitTimetrackingYearResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitTimetrackingYearResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitTimetrackingYearResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['referenceDay'] === 'undefined') {
        reject(new Error('Missing required  parameter: referenceDay'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllCasusCockpitTimetrackingYearURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitTimetrackingYearResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitTimetrackingYearResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitTimetrackingYearResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitTimetrackingYearResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllCasusCockpitTimetrackingYearURL(
    parameters: {
      referenceDay: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/cockpit/timetracking-year/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['referenceDay'] !== 'undefined') {
      queryParameters['referenceDay'] =
        Backend.convertParameterCollectionFormat(
          parameters['referenceDay'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#importCasus
   * @param {} body -
   */
  public async importCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ImportCasusParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ImportCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ImportCasusResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ImportCasusResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.importCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ImportCasusResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ImportCasusResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ImportCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ImportCasusResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public importCasusURL(
    parameters: {
      body: ImportCasusParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/import/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkNameAvailability
   * @param {string} casusName -
   * @param {string} clientName -
   */
  public async checkNameAvailability({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusName: string;
      clientName: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CheckNameAvailabilityResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CheckNameAvailabilityResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CheckNameAvailabilityResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusName'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusName'));
        return;
      }

      if (typeof parameters['clientName'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientName'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.checkNameAvailabilityURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CheckNameAvailabilityResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CheckNameAvailabilityResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CheckNameAvailabilityResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CheckNameAvailabilityResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public checkNameAvailabilityURL(
    parameters: {
      casusName: string;
      clientName: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/name-availability/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusName'] !== 'undefined') {
      queryParameters['casusName'] = Backend.convertParameterCollectionFormat(
        parameters['casusName'],
        ''
      );
    }

    if (typeof parameters['clientName'] !== 'undefined') {
      queryParameters['clientName'] = Backend.convertParameterCollectionFormat(
        parameters['clientName'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkNameAvailabilityByClientId
   * @param {string} casusName -
   * @param {string} clientId -
   * @param {string} groupId -
   */
  public async checkNameAvailabilityByClientId({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusName: string;
      clientId: string;
      groupId?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CheckNameAvailabilityResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CheckNameAvailabilityResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CheckNameAvailabilityResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusName'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusName'));
        return;
      }

      if (typeof parameters['clientId'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.checkNameAvailabilityByClientIdURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CheckNameAvailabilityResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CheckNameAvailabilityResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CheckNameAvailabilityResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CheckNameAvailabilityResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public checkNameAvailabilityByClientIdURL(
    parameters: {
      casusName: string;
      clientId: string;
      groupId?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/name-availability-by-client-id/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusName'] !== 'undefined') {
      queryParameters['casusName'] = Backend.convertParameterCollectionFormat(
        parameters['casusName'],
        ''
      );
    }

    if (typeof parameters['clientId'] !== 'undefined') {
      queryParameters['clientId'] = Backend.convertParameterCollectionFormat(
        parameters['clientId'],
        ''
      );
    }

    if (typeof parameters['groupId'] !== 'undefined') {
      queryParameters['groupId'] = Backend.convertParameterCollectionFormat(
        parameters['groupId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteCasus
   * @param {string} casusId -
   */
  public async deleteCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteCasusURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasus
   * @param {string} casusId -
   */
  public async getCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, GetCasusResponse>
          | ResponseWithBody<400, CasusNotFoundCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, GetCasusResponse>
      | ResponseWithBody<400, CasusNotFoundCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getCasusURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, GetCasusResponse>
      | ResponseWithBody<400, CasusNotFoundCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, GetCasusResponse>
          | ResponseWithBody<400, CasusNotFoundCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, GetCasusResponse>
        | ResponseWithBody<400, CasusNotFoundCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasusArchive
   * @param {} body -
   * @param {string} casusId -
   */
  public async createCasusArchive({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusArchiveParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, CasusArchivingNotPossibleCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, CasusArchivingNotPossibleCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
        body = sanitizeCasusArchiveParams(body);
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createCasusArchiveURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, CasusArchivingNotPossibleCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, CasusArchivingNotPossibleCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, CasusArchivingNotPossibleCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusArchiveURL(
    parameters: {
      body: CasusArchiveParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/archive/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusArchive
   * @param {} body -
   * @param {string} casusId -
   */
  public async updateCasusArchive({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusArchiveUpdateParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeCasusArchiveUpdateParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusArchiveURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusArchiveURL(
    parameters: {
      body: CasusArchiveUpdateParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/archive/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getArchives
   * @param {string} casusId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getArchives({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      sortFields: Array<'DATE' | 'STATUS' | 'DESCRIPTION' | 'ARCHIVE_LOCATION'>;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CasusArchivesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CasusArchivesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CasusArchivesResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getArchivesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CasusArchivesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CasusArchivesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CasusArchivesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CasusArchivesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getArchivesURL(
    parameters: {
      casusId: string;
      sortFields: Array<'DATE' | 'STATUS' | 'DESCRIPTION' | 'ARCHIVE_LOCATION'>;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/archives/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkCasusDeletionPossible
   * @param {string} casusId -
   */
  public async checkCasusDeletionPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: DeleteCasusCheckResult;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, DeleteCasusCheckResult> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, DeleteCasusCheckResult>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.checkCasusDeletionPossibleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, DeleteCasusCheckResult>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          DeleteCasusCheckResult
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as DeleteCasusCheckResult,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, DeleteCasusCheckResult>,
      header: result.header,
      hasError: true,
    };
  }

  public checkCasusDeletionPossibleURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/check-deletion-possible/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setClient
   * @param {} body -
   * @param {string} casusId -
   */
  public async setClient({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetClientParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setClientURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setClientURL(
    parameters: {
      body: SetClientParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/client/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getSingleCasusCockpitFinanceOverview
   * @param {string} casusId -
   * @param {integer} year -
   */
  public async getSingleCasusCockpitFinanceOverview({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      year?: number;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitFinanceOverviewResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitFinanceOverviewResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitFinanceOverviewResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getSingleCasusCockpitFinanceOverviewURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitFinanceOverviewResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitFinanceOverviewResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitFinanceOverviewResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitFinanceOverviewResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getSingleCasusCockpitFinanceOverviewURL(
    parameters: {
      casusId: string;
      year?: number;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/cockpit/finance/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['year'] !== 'undefined') {
      queryParameters['year'] = Backend.convertParameterCollectionFormat(
        parameters['year'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getSingleCasusCockpitInvolvedPeople
   * @param {string} casusId -
   */
  public async getSingleCasusCockpitInvolvedPeople({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitInvolvedPeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitInvolvedPeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitInvolvedPeopleResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getSingleCasusCockpitInvolvedPeopleURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitInvolvedPeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitInvolvedPeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitInvolvedPeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitInvolvedPeopleResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getSingleCasusCockpitInvolvedPeopleURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/cockpit/involved-people/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getSingleCasusCockpitTimelineEvents
   * @param {string} casusId -
   * @param {array} eventContexts -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   */
  public async getSingleCasusCockpitTimelineEvents({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      eventContexts: Array<'DOCUMENT' | 'OBLIGATION' | 'TASK' | 'NOTE'>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CockpitTimelineEventsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CockpitTimelineEventsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CockpitTimelineEventsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['eventContexts'] === 'undefined') {
        reject(new Error('Missing required  parameter: eventContexts'));
        return;
      }

      if (typeof parameters['paginationDirection'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationDirection'));
        return;
      }

      if (typeof parameters['paginationMode'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationMode'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getSingleCasusCockpitTimelineEventsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CockpitTimelineEventsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CockpitTimelineEventsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CockpitTimelineEventsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CockpitTimelineEventsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getSingleCasusCockpitTimelineEventsURL(
    parameters: {
      casusId: string;
      eventContexts: Array<'DOCUMENT' | 'OBLIGATION' | 'TASK' | 'NOTE'>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/cockpit/timeline-events/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['eventContexts'] !== 'undefined') {
      queryParameters['eventContexts'] =
        Backend.convertParameterCollectionFormat(
          parameters['eventContexts'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getContacts
   * @param {string} casusId -
   */
  public async getContacts({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getContactsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getContactsURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/contacts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addCasusDebtor
   * @param {} body -
   * @param {string} casusId -
   */
  public async addCasusDebtor({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusDebtorStoringParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addCasusDebtorURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addCasusDebtorURL(
    parameters: {
      body: CasusDebtorStoringParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/contacts/casus-debtors/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteCasusDebtor
   * @param {string} casusDebtorId -
   * @param {string} casusId -
   */
  public async deleteCasusDebtor({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusDebtorId: string;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, ContactsResponse>
          | ResponseWithBody<400, CasusDebtorInUseCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, ContactsResponse>
      | ResponseWithBody<400, CasusDebtorInUseCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusDebtorId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusDebtorId'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteCasusDebtorURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, ContactsResponse>
      | ResponseWithBody<400, CasusDebtorInUseCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, ContactsResponse>
          | ResponseWithBody<400, CasusDebtorInUseCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, ContactsResponse>
        | ResponseWithBody<400, CasusDebtorInUseCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteCasusDebtorURL(
    parameters: {
      casusDebtorId: string;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/casus-debtors/{casusDebtorId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusDebtorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusDebtorId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusDebtor
   * @param {} body -
   * @param {string} casusDebtorId -
   * @param {string} casusId -
   */
  public async updateCasusDebtor({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusDebtorUpdateParams;
      casusDebtorId: string;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, ContactsResponse>
          | ResponseWithBody<400, CasusDebtorInUseCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, ContactsResponse>
      | ResponseWithBody<400, CasusDebtorInUseCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusDebtorId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusDebtorId'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.updateCasusDebtorURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, ContactsResponse>
      | ResponseWithBody<400, CasusDebtorInUseCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, ContactsResponse>
          | ResponseWithBody<400, CasusDebtorInUseCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, ContactsResponse>
        | ResponseWithBody<400, CasusDebtorInUseCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusDebtorURL(
    parameters: {
      body: CasusDebtorUpdateParams;
      casusDebtorId: string;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/casus-debtors/{casusDebtorId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusDebtorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusDebtorId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoices
   * @param {string} casusDebtorId -
   * @param {string} casusId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getInvoices({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusDebtorId: string;
      casusId: string;
      sortFields: Array<
        'VALUE_DATE' | 'CREATED_AT' | 'TOTAL_AFTER_TAX' | 'REMAINING_AMOUNT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getInvoices_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getInvoices_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_getInvoices_200>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusDebtorId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusDebtorId'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoicesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getInvoices_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getInvoices_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getInvoices_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_getInvoices_200>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoicesURL(
    parameters: {
      casusDebtorId: string;
      casusId: string;
      sortFields: Array<
        'VALUE_DATE' | 'CREATED_AT' | 'TOTAL_AFTER_TAX' | 'REMAINING_AMOUNT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/casus-debtors/{casusDebtorId}/invoices/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusDebtorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusDebtorId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPayments
   * @param {string} casusDebtorId -
   * @param {string} casusId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getPayments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusDebtorId: string;
      casusId: string;
      sortFields: Array<
        'VALUE_DATE' | 'CREATED_AT' | 'TOTAL_AMOUNT' | 'REMAINING_AMOUNT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getPayments_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getPayments_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_getPayments_200>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusDebtorId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusDebtorId'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getPaymentsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getPayments_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getPayments_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getPayments_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_getPayments_200>,
      header: result.header,
      hasError: true,
    };
  }

  public getPaymentsURL(
    parameters: {
      casusDebtorId: string;
      casusId: string;
      sortFields: Array<
        'VALUE_DATE' | 'CREATED_AT' | 'TOTAL_AMOUNT' | 'REMAINING_AMOUNT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/casus-debtors/{casusDebtorId}/payments/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusDebtorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusDebtorId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCasusDebtorFractions
   * @param {} body -
   * @param {string} casusId -
   */
  public async setCasusDebtorFractions({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DefaultInvoiceRecipientFractionsUpdateParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCasusDebtorFractionsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setCasusDebtorFractionsURL(
    parameters: {
      body: DefaultInvoiceRecipientFractionsUpdateParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/default-invoice-recipient-fractions/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDefaultInvoiceRecipients
   * @param {string} casusId -
   */
  public async getDefaultInvoiceRecipients({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: DefaultInvoiceRecipientsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, DefaultInvoiceRecipientsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, DefaultInvoiceRecipientsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getDefaultInvoiceRecipientsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, DefaultInvoiceRecipientsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          DefaultInvoiceRecipientsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as DefaultInvoiceRecipientsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        DefaultInvoiceRecipientsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getDefaultInvoiceRecipientsURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/default-invoice-recipients/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createInvolvedParty
   * @param {} body -
   * @param {string} casusId -
   */
  public async createInvolvedParty({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvolvedPartyStoringParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeInvolvedPartyStoringParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createInvolvedPartyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createInvolvedPartyURL(
    parameters: {
      body: InvolvedPartyStoringParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/contacts/involved-parties/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteInvolvedParty
   * @param {string} casusId -
   * @param {string} involvedPartyId -
   */
  public async deleteInvolvedParty({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      involvedPartyId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['involvedPartyId'] === 'undefined') {
          reject(new Error('Missing required  parameter: involvedPartyId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteInvolvedPartyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteInvolvedPartyURL(
    parameters: {
      casusId: string;
      involvedPartyId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/involved-parties/{involvedPartyId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{involvedPartyId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['involvedPartyId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvolvedParty
   * @param {string} casusId -
   * @param {string} involvedPartyId -
   */
  public async getInvolvedParty({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      involvedPartyId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvolvedPartyResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvolvedPartyResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvolvedPartyResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['involvedPartyId'] === 'undefined') {
          reject(new Error('Missing required  parameter: involvedPartyId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvolvedPartyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvolvedPartyResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvolvedPartyResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvolvedPartyResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvolvedPartyResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvolvedPartyURL(
    parameters: {
      casusId: string;
      involvedPartyId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/involved-parties/{involvedPartyId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{involvedPartyId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['involvedPartyId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateInvolvedParty
   * @param {} body -
   * @param {string} casusId -
   * @param {string} involvedPartyId -
   */
  public async updateInvolvedParty({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvolvedPartyStoringParams;
      casusId: string;
      involvedPartyId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeInvolvedPartyStoringParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['involvedPartyId'] === 'undefined') {
          reject(new Error('Missing required  parameter: involvedPartyId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateInvolvedPartyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateInvolvedPartyURL(
    parameters: {
      body: InvolvedPartyStoringParams;
      casusId: string;
      involvedPartyId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/contacts/involved-parties/{involvedPartyId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{involvedPartyId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['involvedPartyId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasusArchiveDestroy
   * @param {} body -
   * @param {string} casusId -
   */
  public async createCasusArchiveDestroy({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusArchiveDestroyParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeCasusArchiveDestroyParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createCasusArchiveDestroyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusArchiveDestroyURL(
    parameters: {
      body: CasusArchiveDestroyParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/destroy-archive/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusArchiveDestroy
   * @param {} body -
   * @param {string} casusId -
   */
  public async updateCasusArchiveDestroy({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusArchiveDestroyParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeCasusArchiveDestroyParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusArchiveDestroyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusArchiveDestroyURL(
    parameters: {
      body: CasusArchiveDestroyParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/destroy-archive/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getLatestArchivingLog
   * @param {string} casusId -
   */
  public async getLatestArchivingLog({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: LatestCasusArchivingLogResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, LatestCasusArchivingLogResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, LatestCasusArchivingLogResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getLatestArchivingLogURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, LatestCasusArchivingLogResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          LatestCasusArchivingLogResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as LatestCasusArchivingLogResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        LatestCasusArchivingLogResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getLatestArchivingLogURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/latest-archiving-log/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPeople
   * @param {string} casusId -
   */
  public async getPeople({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getPeopleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getPeopleURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/people/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#removeAuthorizeGroup
   * @param {string} casusId -
   * @param {string} groupId -
   */
  public async removeAuthorizeGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      groupId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['groupId'] === 'undefined') {
          reject(new Error('Missing required  parameter: groupId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.removeAuthorizeGroupURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public removeAuthorizeGroupURL(
    parameters: {
      casusId: string;
      groupId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/people/authorizations/groups/{groupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{groupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['groupId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addAuthorizedGroup
   * @param {string} casusId -
   * @param {string} groupId -
   */
  public async addAuthorizedGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      groupId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['groupId'] === 'undefined') {
          reject(new Error('Missing required  parameter: groupId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.addAuthorizedGroupURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addAuthorizedGroupURL(
    parameters: {
      casusId: string;
      groupId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/people/authorizations/groups/{groupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{groupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['groupId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#removeAuthorizedUser
   * @param {string} casusId -
   * @param {string} userIdOfAuthorizedUser -
   */
  public async removeAuthorizedUser({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      userIdOfAuthorizedUser: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['userIdOfAuthorizedUser'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: userIdOfAuthorizedUser')
          );
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.removeAuthorizedUserURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public removeAuthorizedUserURL(
    parameters: {
      casusId: string;
      userIdOfAuthorizedUser: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/people/authorizations/users/{userIdOfAuthorizedUser}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{userIdOfAuthorizedUser}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['userIdOfAuthorizedUser'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addAuthorizedUser
   * @param {string} casusId -
   * @param {string} userIdOfAuthorizedUser -
   */
  public async addAuthorizedUser({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      userIdOfAuthorizedUser: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['userIdOfAuthorizedUser'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: userIdOfAuthorizedUser')
          );
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.addAuthorizedUserURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addAuthorizedUserURL(
    parameters: {
      casusId: string;
      userIdOfAuthorizedUser: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/people/authorizations/users/{userIdOfAuthorizedUser}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{userIdOfAuthorizedUser}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['userIdOfAuthorizedUser'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCollaborator
   * @param {} body -
   * @param {string} casusId -
   */
  public async createCollaborator({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CollaboratorParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createCollaboratorURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createCollaboratorURL(
    parameters: {
      body: CollaboratorParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/people/collaborators/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteCollaborator
   * @param {string} casusId -
   * @param {string} collaboratorId -
   */
  public async deleteCollaborator({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      collaboratorId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['collaboratorId'] === 'undefined') {
          reject(new Error('Missing required  parameter: collaboratorId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteCollaboratorURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteCollaboratorURL(
    parameters: {
      casusId: string;
      collaboratorId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/people/collaborators/{collaboratorId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{collaboratorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['collaboratorId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCollaborator
   * @param {} body -
   * @param {string} casusId -
   * @param {string} collaboratorId -
   */
  public async updateCollaborator({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CollaboratorParams;
      casusId: string;
      collaboratorId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['collaboratorId'] === 'undefined') {
          reject(new Error('Missing required  parameter: collaboratorId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCollaboratorURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCollaboratorURL(
    parameters: {
      body: CollaboratorParams;
      casusId: string;
      collaboratorId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/people/collaborators/{collaboratorId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{collaboratorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['collaboratorId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setResponsiblePerson
   * @param {} body -
   * @param {string} casusId -
   */
  public async setResponsiblePerson({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ResponsiblePersonUpdateParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeopleResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeopleResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeopleResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setResponsiblePersonURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeopleResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeopleResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeopleResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeopleResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setResponsiblePersonURL(
    parameters: {
      body: ResponsiblePersonUpdateParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/people/responsible-person/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getSettings
   * @param {string} casusId -
   */
  public async getSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getSettingsURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setAlertMessage
   * @param {} body -
   * @param {string} casusId -
   */
  public async setAlertMessage({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetAlertMessageParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeSetAlertMessageParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setAlertMessageURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setAlertMessageURL(
    parameters: {
      body: SetAlertMessageParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/alert-message/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCasusFlags
   * @param {} body -
   * @param {string} casusId -
   */
  public async setCasusFlags({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetCasusFlagsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCasusFlagsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setCasusFlagsURL(
    parameters: {
      body: SetCasusFlagsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/casus-flags/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCasusGroup
   * @param {} body -
   * @param {string} casusId -
   */
  public async setCasusGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetCasusGroupParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCasusGroupURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setCasusGroupURL(
    parameters: {
      body: SetCasusGroupParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/casus-group/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCharacteristics
   * @param {} body -
   * @param {string} casusId -
   */
  public async setCharacteristics({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetCharacteristicsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeSetCharacteristicsParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCharacteristicsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setCharacteristicsURL(
    parameters: {
      body: SetCharacteristicsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/characteristics/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCorrespondenceLocale
   * @param {} body -
   * @param {string} casusId -
   */
  public async setCorrespondenceLocale({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetCorrespondenceLocaleParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCorrespondenceLocaleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setCorrespondenceLocaleURL(
    parameters: {
      body: SetCorrespondenceLocaleParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/casus/{casusId}/settings/correspondence-locale/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCurrency
   * @param {} body -
   * @param {string} casusId -
   */
  public async setCurrency({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetCurrencyParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCurrencyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setCurrencyURL(
    parameters: {
      body: SetCurrencyParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/currency/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setDescription
   * @param {} body -
   * @param {string} casusId -
   */
  public async setDescription({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetDescriptionParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeSetDescriptionParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setDescriptionURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setDescriptionURL(
    parameters: {
      body: SetDescriptionParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/description/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setHourlyRateSettings
   * @param {} body -
   * @param {string} casusId -
   */
  public async setHourlyRateSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetHourlyRateSettingsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setHourlyRateSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setHourlyRateSettingsURL(
    parameters: {
      body: SetHourlyRateSettingsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/hourly-rates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setInvoiceSettings
   * @param {} body -
   * @param {string} casusId -
   */
  public async setInvoiceSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetInvoiceSettingsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setInvoiceSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setInvoiceSettingsURL(
    parameters: {
      body: SetInvoiceSettingsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/invoice-settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setLockState
   * @param {} body -
   * @param {string} casusId -
   */
  public async setLockState({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetLockStateParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setLockStateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setLockStateURL(
    parameters: {
      body: SetLockStateParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/lock-state/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setOfficeLocation
   * @param {} body -
   * @param {string} casusId -
   */
  public async setOfficeLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetOfficeLocationParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setOfficeLocationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setOfficeLocationURL(
    parameters: {
      body: SetOfficeLocationParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/office-location/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setSpecialityField
   * @param {} body -
   * @param {string} casusId -
   */
  public async setSpecialityField({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetSpecialityFieldParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setSpecialityFieldURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setSpecialityFieldURL(
    parameters: {
      body: SetSpecialityFieldParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/speciality-field/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setStartDate
   * @param {} body -
   * @param {string} casusId -
   */
  public async setStartDate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetStartDateParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SettingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SettingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SettingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setStartDateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SettingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SettingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SettingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SettingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setStartDateURL(
    parameters: {
      body: SetStartDateParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/settings/start-date/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasusUnarchive
   * @param {} body -
   * @param {string} casusId -
   */
  public async createCasusUnarchive({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusUnarchiveParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeCasusUnarchiveParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createCasusUnarchiveURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusUnarchiveURL(
    parameters: {
      body: CasusUnarchiveParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/unarchive/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusUnarchive
   * @param {} body -
   * @param {string} casusId -
   */
  public async updateCasusUnarchive({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusUnarchiveParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
          body = sanitizeCasusUnarchiveParams(body);
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusUnarchiveURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusUnarchiveURL(
    parameters: {
      body: CasusUnarchiveParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/casus/{casusId}/unarchive/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchClients
   * @param {string} clientTypeFilter -
   * @param {array} ids -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchClients({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientTypeFilter?: 'CONTACT' | 'AD_HOC';
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchClientsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchClientsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchClientsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchClientsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchClientsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchClientsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchClientsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchClientsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchClientsURL(
    parameters: {
      clientTypeFilter?: 'CONTACT' | 'AD_HOC';
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/clients/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['clientTypeFilter'] !== 'undefined') {
      queryParameters['clientTypeFilter'] =
        Backend.convertParameterCollectionFormat(
          parameters['clientTypeFilter'],
          ''
        );
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchContacts
   * @param {string} contactType -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchContacts({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      contactType?: 'NATURAL_PERSON' | 'LEGAL_ENTITY';
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchContactResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchContactResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchContactResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchContactsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchContactResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchContactResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchContactResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchContactResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchContactsURL(
    parameters: {
      contactType?: 'NATURAL_PERSON' | 'LEGAL_ENTITY';
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/contacts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['contactType'] !== 'undefined') {
      queryParameters['contactType'] = Backend.convertParameterCollectionFormat(
        parameters['contactType'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getRelatedAddresses
   * @param {string} contactId -
   */
  public async getRelatedAddresses({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      contactId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetRelatedAddressesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetRelatedAddressesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetRelatedAddressesResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['contactId'] === 'undefined') {
          reject(new Error('Missing required  parameter: contactId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getRelatedAddressesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetRelatedAddressesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetRelatedAddressesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetRelatedAddressesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetRelatedAddressesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getRelatedAddressesURL(
    parameters: {
      contactId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/contacts/{contactId}/related-addresses/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{contactId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['contactId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getRelatedEmailAddresses
   * @param {string} contactId -
   */
  public async getRelatedEmailAddresses({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      contactId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetRelatedEmailAddressesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetRelatedEmailAddressesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetRelatedEmailAddressesResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['contactId'] === 'undefined') {
        reject(new Error('Missing required  parameter: contactId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getRelatedEmailAddressesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetRelatedEmailAddressesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetRelatedEmailAddressesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetRelatedEmailAddressesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetRelatedEmailAddressesResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getRelatedEmailAddressesURL(
    parameters: {
      contactId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/contacts/{contactId}/related-email-addresses/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{contactId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['contactId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteCasusGroup
   * @param {string} groupId -
   */
  public async deleteCasusGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      groupId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['groupId'] === 'undefined') {
          reject(new Error('Missing required  parameter: groupId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteCasusGroupURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteCasusGroupURL(
    parameters: {
      groupId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/groups/{groupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{groupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['groupId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateCasusGroup
   * @param {} body -
   * @param {string} groupId -
   */
  public async updateCasusGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusGroupUpdateParams;
      groupId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['groupId'] === 'undefined') {
          reject(new Error('Missing required  parameter: groupId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateCasusGroupURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateCasusGroupURL(
    parameters: {
      body: CasusGroupUpdateParams;
      groupId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/groups/{groupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{groupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['groupId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasusMasterData
   */
  public async getCasusMasterData({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CasusMasterData;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CasusMasterData> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CasusMasterData>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getCasusMasterDataURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CasusMasterData>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CasusMasterData
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CasusMasterData,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CasusMasterData>,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusMasterDataURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/masterdata/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getOfficeLocationsAdmin
   */
  public async getOfficeLocationsAdmin({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getOfficeLocationsAdmin_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_getOfficeLocationsAdmin_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getOfficeLocationsAdmin_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getOfficeLocationsAdminURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getOfficeLocationsAdmin_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getOfficeLocationsAdmin_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getOfficeLocationsAdmin_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getOfficeLocationsAdmin_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getOfficeLocationsAdminURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/office-locations/admin/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createOfficeLocation
   * @param {} body -
   */
  public async createOfficeLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateOfficeLocationParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreatedOfficeLocationResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreatedOfficeLocationResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CreatedOfficeLocationResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createOfficeLocationURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreatedOfficeLocationResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreatedOfficeLocationResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreatedOfficeLocationResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CreatedOfficeLocationResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createOfficeLocationURL(
    parameters: {
      body: CreateOfficeLocationParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/office-locations/admin/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteOfficeLocation
   * @param {string} officeLocationId -
   */
  public async deleteOfficeLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      officeLocationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['officeLocationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: officeLocationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteOfficeLocationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteOfficeLocationURL(
    parameters: {
      officeLocationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/office-locations/admin/{officeLocationId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{officeLocationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateOfficeLocation
   * @param {} body -
   * @param {string} officeLocationId -
   */
  public async updateOfficeLocation({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UpdateOfficeLocationParams;
      officeLocationId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['officeLocationId'] === 'undefined') {
          reject(new Error('Missing required  parameter: officeLocationId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateOfficeLocationURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateOfficeLocationURL(
    parameters: {
      body: UpdateOfficeLocationParams;
      officeLocationId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/office-locations/admin/{officeLocationId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{officeLocationId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getSpecialityFields
   */
  public async getSpecialityFields({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getSpecialityFields_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getSpecialityFields_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getSpecialityFields_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getSpecialityFieldsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getSpecialityFields_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getSpecialityFields_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getSpecialityFields_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getSpecialityFields_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getSpecialityFieldsURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/speciality-fields/admin/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createSpecialityField
   * @param {} body -
   */
  public async createSpecialityField({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateSpecialityFieldParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SpecialityFieldCreatedResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SpecialityFieldCreatedResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, SpecialityFieldCreatedResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createSpecialityFieldURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SpecialityFieldCreatedResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SpecialityFieldCreatedResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SpecialityFieldCreatedResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        SpecialityFieldCreatedResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createSpecialityFieldURL(
    parameters: {
      body: CreateSpecialityFieldParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/speciality-fields/admin/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteSpecialityField
   * @param {string} specialityFieldId -
   */
  public async deleteSpecialityField({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      specialityFieldId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['specialityFieldId'] === 'undefined') {
          reject(new Error('Missing required  parameter: specialityFieldId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteSpecialityFieldURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteSpecialityFieldURL(
    parameters: {
      specialityFieldId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/speciality-fields/admin/{specialityFieldId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{specialityFieldId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['specialityFieldId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateSpecialityField
   * @param {} body -
   * @param {string} specialityFieldId -
   */
  public async updateSpecialityField({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UpdateSpecialityFieldParams;
      specialityFieldId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['specialityFieldId'] === 'undefined') {
          reject(new Error('Missing required  parameter: specialityFieldId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateSpecialityFieldURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public updateSpecialityFieldURL(
    parameters: {
      body: UpdateSpecialityFieldParams;
      specialityFieldId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/speciality-fields/admin/{specialityFieldId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{specialityFieldId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['specialityFieldId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setFavorite
   * @param {} body -
   * @param {string} casusId -
   */
  public async setFavorite({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FavoriteParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setFavoriteURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setFavoriteURL(
    parameters: {
      body: FavoriteParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/user-preferences/current-user/casus/{casusId}/favorite/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setMostRecentlyUsedCasus
   * @param {} body -
   */
  public async setMostRecentlyUsedCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: MostRecentlyUsedParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.setMostRecentlyUsedCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setMostRecentlyUsedCasusURL(
    parameters: {
      body: MostRecentlyUsedParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/casus-management/user-preferences/current-user/most-recently-used/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchCasusGroups
   * @param {string} clientId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchCasusGroups({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientId: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PageCasusGroup;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PageCasusGroup> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PageCasusGroup>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['clientId'] === 'undefined') {
          reject(new Error('Missing required  parameter: clientId'));
          return;
        }

        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchCasusGroupsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PageCasusGroup>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PageCasusGroup
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PageCasusGroup,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PageCasusGroup>,
      header: result.header,
      hasError: true,
    };
  }

  public searchCasusGroupsURL(
    parameters: {
      clientId: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/{clientId}/groups/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{clientId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['clientId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCasusGroup
   * @param {} body -
   * @param {string} clientId -
   */
  public async createCasusGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CasusGroupCreationParams;
      clientId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_createCasusGroup_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_createCasusGroup_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_createCasusGroup_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['clientId'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createCasusGroupURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_createCasusGroup_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_createCasusGroup_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_createCasusGroup_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_createCasusGroup_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createCasusGroupURL(
    parameters: {
      body: CasusGroupCreationParams;
      clientId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/{clientId}/groups/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{clientId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['clientId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#validateCasusGroupName
   * @param {string} clientId -
   * @param {string} name -
   */
  public async validateCasusGroupName({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientId: string;
      name: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CasusGroupNameValidationResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CasusGroupNameValidationResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CasusGroupNameValidationResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['clientId'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientId'));
        return;
      }

      if (typeof parameters['name'] === 'undefined') {
        reject(new Error('Missing required  parameter: name'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.validateCasusGroupNameURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CasusGroupNameValidationResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CasusGroupNameValidationResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CasusGroupNameValidationResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CasusGroupNameValidationResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public validateCasusGroupNameURL(
    parameters: {
      clientId: string;
      name: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/casus-management/{clientId}/groups/validate/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{clientId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['clientId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['name'] !== 'undefined') {
      queryParameters['name'] = Backend.convertParameterCollectionFormat(
        parameters['name'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }
}

const backend = new Backend(
  process.env.REACT_APP_SERVER_URL || '/api',
  (error) => {
    if (error.status === 401) {
      window.location.href =
        '/login/?redirect=' +
        encodeURIComponent(
          window.location.pathname +
            window.location.search +
            window.location.hash
        );
    }
  }
);

backend.setConfigureRequestHandler(
  (request: SuperAgentRequest): SuperAgentRequest => {
    if (new RegExp('/download', 'u').test(request.url)) {
      request.responseType('blob');
    }

    return request;
  }
);

export default backend;
